import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogTitle, DialogContent,
  DialogActions, Divider, FormControl, IconButton,
  InputAdornment, InputLabel, ListItemIcon, ListItemText, Menu, MenuItem,
  Paper, Select, Skeleton,
  Snackbar, Stack, TextField, Tooltip, Typography, alpha,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import BlockRoundedIcon from '@mui/icons-material/BlockRounded';
import LockOpenRoundedIcon from '@mui/icons-material/LockOpenRounded';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import InboxRoundedIcon from '@mui/icons-material/InboxRounded';
import LoginRoundedIcon from '@mui/icons-material/LoginRounded';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import TimelapseRoundedIcon from '@mui/icons-material/TimelapseRounded';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import WorkspacesOutlinedIcon from '@mui/icons-material/WorkspacesOutlined';
import FlagRoundedIcon from '@mui/icons-material/FlagRounded';
import EditCalendarRoundedIcon from '@mui/icons-material/EditCalendarRounded';
import PendingActionsRoundedIcon from '@mui/icons-material/PendingActionsRounded';
import GavelRoundedIcon from '@mui/icons-material/GavelRounded';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import MoreHorizRoundedIcon from '@mui/icons-material/MoreHorizRounded';
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded';
import AssignmentIndRoundedIcon from '@mui/icons-material/AssignmentIndRounded';
import EscalatorWarningRoundedIcon from '@mui/icons-material/EscalatorWarningRounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import AlternateEmailRoundedIcon from '@mui/icons-material/AlternateEmailRounded';
import SwapVertRoundedIcon from '@mui/icons-material/SwapVertRounded';
import PriorityHighRoundedIcon from '@mui/icons-material/PriorityHighRounded';
import EmailRoundedIcon from '@mui/icons-material/EmailRounded';
import NotificationsActiveRoundedIcon from '@mui/icons-material/NotificationsActiveRounded';

/* ─────────────────────────────────────────────────────────────────────────────
 * Tokens
 * ──────────────────────────────────────────────────────────────────────────── */
const T = {
  brand: '#3D8FA3', brandHover: '#327C8E', brandLight: '#E7F4F7', brandSoft: '#F2FAFC',
  ink: '#0F172A', ink700: '#334155', ink500: '#64748B', ink400: '#94A3B8', ink300: '#CBD5E1',
  line: '#E2E8F0', lineSoft: '#EEF2F6',
  surface: '#F8FAFC', surfaceAlt: '#F1F5F9', white: '#FFFFFF',
  success: '#10B981', successBg: '#ECFDF5', successInk: '#059669',
  warning: '#F59E0B', warningBg: '#FEF3C7', warningInk: '#92400E',
  error:   '#EF4444', errorBg:   '#FEF2F2', errorInk:   '#DC2626',
  info:    '#3D8FA3', infoBg:    '#E7F4F7', infoInk:    '#1F6B7E',
};

const cardShell = {
  bgcolor: T.white,
  borderRadius: '14px',
  border: `1px solid ${T.line}`,
  boxShadow: '0 1px 2px rgba(15,23,42,.04)',
};

/* ─────────────────────────────────────────────────────────────────────────────
 * Static
 * ──────────────────────────────────────────────────────────────────────────── */
const TAB_DEFS = [
  { value: 'all',        label: 'Të gjitha'    },
  { value: 'open',       label: 'Hapur'        },
  { value: 'in_review',  label: 'Në shqyrtim'  },
  { value: 'needs_info', label: 'Kërkohen sqarime' },
  { value: 'approved',   label: 'Aprovuar'     },
  { value: 'rejected',   label: 'Refuzuar'     },
];

const STATUS_LABEL = {
  open: 'Hapur', in_review: 'Në shqyrtim', needs_info: 'Kërkohen sqarime',
  approved: 'Aprovuar', rejected: 'Refuzuar',
};

const STATUS_TONE = {
  open: 'warning', in_review: 'info', needs_info: 'purple',
  approved: 'success', rejected: 'error',
};

const PRIORITY_OPTS = [
  { value: 'low',    label: 'E ulët',     dot: '#94A3B8' },
  { value: 'normal', label: 'Normale',    dot: '#3D8FA3' },
  { value: 'high',   label: 'E lartë',    dot: '#F59E0B' },
  { value: 'urgent', label: 'Urgjent',    dot: '#EF4444' },
];
const PRIORITY_LABEL = Object.fromEntries(PRIORITY_OPTS.map(p => [p.value, p.label]));

const SORT_OPTS = [
  { value: 'newest',   label: 'Më të rejat'   },
  { value: 'oldest',   label: 'Më të vjetrat' },
  { value: 'priority', label: 'Prioriteti'    },
  { value: 'status',   label: 'Statusi'       },
  { value: 'worker',   label: 'Punëtori'      },
];

const TYPE_OPTS = [
  { value: 'correction',      label: 'Korrigjim i përgjithshëm' },
  { value: 'forgot_checkin',  label: 'Harrova check-in'         },
  { value: 'forgot_checkout', label: 'Harrova check-out'        },
  { value: 'wrong_time',      label: 'Orë e gabuar'             },
  { value: 'overtime',        label: 'Orë shtesë'               },
  { value: 'other',           label: 'Tjetër'                   },
];
const TYPE_LABEL = Object.fromEntries(TYPE_OPTS.map(t => [t.value, t.label]));

const fmtDate = (v) => {
  if (!v) return '—';
  return new Date(v).toLocaleString('sq-AL', {
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit',
  });
};
const fmtDateOnly = (v) => {
  if (!v) return '—';
  return new Date(v).toLocaleDateString('sq-AL', { year: 'numeric', month: '2-digit', day: '2-digit' });
};
const fmtTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return isNaN(d) ? '' : d.toLocaleTimeString('sq-AL', { hour: '2-digit', minute: '2-digit' });
};
const relativeFromNow = (v) => {
  if (!v) return '';
  const diff = Date.now() - new Date(v).getTime();
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return 'tani';
  const min = Math.floor(sec / 60); if (min < 60) return `${min}m`;
  const hr  = Math.floor(min / 60); if (hr  < 24) return `${hr}h`;
  const day = Math.floor(hr  / 24); if (day < 7)  return `${day}d`;
  return new Date(v).toLocaleDateString('sq-AL', { day: '2-digit', month: '2-digit' });
};
const buildIso = (d, t) => (!d || !t) ? null : `${d}T${t}:00`;

/* ─────────────────────────────────────────────────────────────────────────────
 * Auth (unchanged behavior)
 * ──────────────────────────────────────────────────────────────────────────── */
function readStoredUser() {
  try {
    const raw = sessionStorage.getItem('logedInUser') || localStorage.getItem('logedInUser') || '';
    if (raw) return JSON.parse(raw);
    const cookie = document.cookie.split(';').find((c) => c.trim().startsWith('logedInUser='));
    if (cookie) {
      const val = decodeURIComponent(cookie.split('=').slice(1).join('='));
      return JSON.parse(val);
    }
  } catch { /* ignore */ }
  return {};
}
function getAuthHeaders() { const t = readStoredUser()?.token || ''; return t ? { Authorization: `Bearer ${t}` } : {}; }
function getCurrentUser() {
  const u = readStoredUser() || {};
  const base = u.data || u || {};
  // worker_id is stored at the session ROOT by SignIn (not inside `data`), so
  // surface it here regardless of which level it lives on.
  const worker_id = base.worker_id ?? base.workerId ?? base.WorkerId
    ?? u.worker_id ?? u.workerId ?? u.WorkerId ?? null;
  return { ...base, worker_id, WorkerId: worker_id };
}
function isReviewer(user) {
  // user_role is what the backend returns on login; some older code paths
  // stored a plain `role`. Roles array (from /me) wins if present.
  const candidates = [
    user?.user_role, user?.role, user?.Role,
    ...((user?.roles || []).map((r) => (r?.name || r?.role_name || r || '').toString())),
  ];
  return candidates
    .map((r) => (r || '').toString().toLowerCase().trim())
    .some((r) => ['super_admin', 'admin', 'company_admin', 'hr_manager', 'team_lead', 'menaxher', 'manager'].includes(r));
}

const API = process.env.REACT_APP_API_URL || '';

/* ─────────────────────────────────────────────────────────────────────────────
 * Service — all backend calls in one place so the page body stays readable.
 * Every action returns { ok, data, error } so the UI can show a toast without
 * needing to know HTTP details.
 * ──────────────────────────────────────────────────────────────────────────── */
async function apiJSON(path, opts = {}) {
  try {
    const r = await fetch(`${API}${path}`, {
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      ...opts,
      body: opts.body && typeof opts.body !== 'string' ? JSON.stringify(opts.body) : opts.body,
    });
    const json = await r.json().catch(() => ({}));
    if (!r.ok || json.success === false) {
      return { ok: false, error: json.error || r.statusText, data: null };
    }
    return { ok: true, data: json.data ?? json, status: r.status };
  } catch (e) {
    return { ok: false, error: e?.message || 'Network error', data: null };
  }
}

const correctionService = {
  list:        (params) => apiJSON(`/api/attendance-requests?${new URLSearchParams(params).toString()}`),
  detail:      (id)     => apiJSON(`/api/attendance-requests/${id}`),
  audit:       (id)     => apiJSON(`/api/attendance-requests/${id}/audit`),
  create:      (body)   => apiJSON(`/api/attendance-requests`, { method: 'POST', body }),
  update:      (id, body) => apiJSON(`/api/attendance-requests/${id}`, { method: 'PUT', body }),
  comment:     (id, body) => apiJSON(`/api/attendance-requests/${id}/comment`, { method: 'POST', body }),
  assign:      (id, body) => apiJSON(`/api/attendance-requests/${id}/assign`,   { method: 'PATCH', body }),
  escalate:    (id, body) => apiJSON(`/api/attendance-requests/${id}/escalate`, { method: 'PATCH', body }),
};

/* ─────────────────────────────────────────────────────────────────────────────
 * Local read-tracker — per-user, last-seen-at per request. Persists across
 * sessions in localStorage so unread badges keep working after refresh.
 * ──────────────────────────────────────────────────────────────────────────── */
function readTrackerKey(userId) { return `attCorrSeen:${userId || 'anon'}`; }

function loadReadMap(userId) {
  try { return JSON.parse(localStorage.getItem(readTrackerKey(userId)) || '{}') || {}; }
  catch { return {}; }
}
function saveReadMap(userId, map) {
  try { localStorage.setItem(readTrackerKey(userId), JSON.stringify(map || {})); } catch { /* quota */ }
}

/* ─────────────────────────────────────────────────────────────────────────────
 * CSV export — emits a workbook the user can open in Excel / Google Sheets.
 * No backend round-trip needed; uses the rows already in memory.
 * ──────────────────────────────────────────────────────────────────────────── */
function exportRequestsCSV(rows) {
  const esc = (v) => `"${String(v ?? '').replace(/"/g, '""').replace(/\n/g, ' ')}"`;
  const headers = [
    'request_number', 'worker_name', 'type', 'status', 'priority',
    'request_date', 'req_check_in', 'req_check_out', 'req_break_min',
    'description', 'assigned_to_name', 'escalated_to_hr',
    'reviewed_by_name', 'reviewed_at', 'created_at', 'updated_at',
  ];
  const lines = [headers.join(',')].concat(
    rows.map(r => headers.map(h => esc(r[h])).join(','))
  );
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = url;
  a.download = `attendance-corrections-${new Date().toISOString().slice(0,10)}.csv`;
  document.body.appendChild(a); a.click();
  setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 0);
}

/* ─────────────────────────────────────────────────────────────────────────────
 * Mention parsing — turns "@first.last hello" into clickable chips inline.
 * ──────────────────────────────────────────────────────────────────────────── */
const MENTION_RE = /(@[\w.+\-:@]+)/g;
function renderWithMentions(text) {
  if (!text) return null;
  const parts = String(text).split(MENTION_RE);
  return parts.map((part, i) => {
    if (part.startsWith('@') && /[\w]/.test(part[1] || '')) {
      return (
        <Box
          key={i}
          component="span"
          sx={{
            display: 'inline-flex', alignItems: 'center', gap: 0.25,
            px: 0.5, mx: 0.125, borderRadius: '4px',
            bgcolor: T.brandLight, color: T.brand,
            fontWeight: 600, fontSize: 'inherit', lineHeight: 1.5,
          }}
        >
          <AlternateEmailRoundedIcon sx={{ fontSize: 11 }} />
          {part.slice(1)}
        </Box>
      );
    }
    return <React.Fragment key={i}>{part}</React.Fragment>;
  });
}

/* ─────────────────────────────────────────────────────────────────────────────
 * Tone palette + atoms
 * ──────────────────────────────────────────────────────────────────────────── */
const TONE = {
  success: { bg: T.successBg, fg: T.successInk, dot: T.success },
  warning: { bg: T.warningBg, fg: T.warningInk, dot: T.warning },
  error:   { bg: T.errorBg,   fg: T.errorInk,   dot: T.error   },
  info:    { bg: T.infoBg,    fg: T.infoInk,    dot: T.info    },
  purple:  { bg: '#F3E8FF',   fg: '#6B21A8',    dot: '#A855F7' },
  neutral: { bg: T.surfaceAlt, fg: T.ink500,    dot: T.ink400  },
};

function StatusBadge({ status, size = 'sm' }) {
  const tone = TONE[STATUS_TONE[status]] || TONE.neutral;
  const lg = size === 'lg';
  return (
    <Box sx={{
      display: 'inline-flex', alignItems: 'center', gap: 0.625,
      height: lg ? 24 : 20, px: lg ? 1.25 : 0.875, borderRadius: '999px',
      bgcolor: tone.bg, color: tone.fg,
      fontSize: lg ? 12 : 11, fontWeight: 600, lineHeight: 1,
      whiteSpace: 'nowrap',
    }}>
      <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: tone.dot }} />
      {STATUS_LABEL[status] || status}
    </Box>
  );
}

function Avatar({ name, size = 32, tone = 'brand' }) {
  const initials = (name || '?')
    .split(' ').filter(Boolean).slice(0, 2)
    .map(s => s[0]).join('').toUpperCase() || '?';
  const tones = {
    brand:   { bg: T.brandLight, fg: T.brand   },
    neutral: { bg: T.surfaceAlt, fg: T.ink500  },
  };
  const c = tones[tone] || tones.brand;
  return (
    <Box sx={{
      width: size, height: size, flexShrink: 0,
      borderRadius: '50%',
      bgcolor: c.bg, color: c.fg,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: size * 0.36, fontWeight: 600, letterSpacing: 0.2,
    }}>
      {initials}
    </Box>
  );
}

function SectionHeading({ children, count, action, sx }) {
  return (
    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.875, ...sx }}>
      <Typography sx={{
        fontSize: 11, fontWeight: 700, color: T.ink400,
        textTransform: 'uppercase', letterSpacing: 0.7,
      }}>
        {children}
      </Typography>
      {count != null && (
        <Box sx={{
          minWidth: 18, height: 16, px: 0.625, borderRadius: '999px',
          bgcolor: T.surfaceAlt, color: T.ink500,
          fontSize: 10, fontWeight: 700,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        }}>{count}</Box>
      )}
      {action && <Box sx={{ ml: 'auto' }}>{action}</Box>}
    </Stack>
  );
}

function SoftDivider({ my = 1.25 }) {
  return <Box sx={{ height: 1, bgcolor: T.lineSoft, my }} />;
}

/* ─────────────────────────────────────────────────────────────────────────────
 * ActingAsBar — visible at the top of the inbox so the user can confirm which
 * authenticated identity will be recorded on every action they take. If the
 * logged-in user is not linked to a worker record, we warn instead of letting
 * them silently fail on submit.
 * ──────────────────────────────────────────────────────────────────────────── */
function ActingAsBar({ workerName, userEmail, userRole, workerId, isManager, canCreateOwnRequest }) {
  const warning = !canCreateOwnRequest && !isManager;
  return (
    <Box
      sx={{
        px: 1.25, py: 0.75,
        display: 'flex', alignItems: 'center', gap: 1,
        borderTop: `1px solid ${T.lineSoft}`,
        bgcolor: warning ? T.warningBg : T.surface,
      }}
    >
      <Avatar name={workerName} size={26} />
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Stack direction="row" alignItems="center" spacing={0.625}>
          <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: warning ? T.warningInk : T.ink, lineHeight: 1.2 }} noWrap>
            {workerName}
          </Typography>
          {isManager && (
            <Box sx={{
              px: 0.625, height: 15, borderRadius: '4px',
              bgcolor: T.brandLight, color: T.brand,
              fontSize: 9.5, fontWeight: 700, letterSpacing: 0.4,
              display: 'inline-flex', alignItems: 'center',
              textTransform: 'uppercase',
            }}>Reviewer</Box>
          )}
        </Stack>
        <Typography
          sx={{
            fontSize: 10.5, color: warning ? T.warningInk : T.ink500, lineHeight: 1.3,
            fontVariantNumeric: 'tabular-nums',
          }}
          noWrap
          title={warning ? 'Llogaria juaj nuk ka worker_id — kontaktoni HR' : `${userEmail} · ${userRole || '—'}`}
        >
          {warning
            ? '⚠ Nuk je i lidhur me një profil punëtori'
            : <>{userEmail}{userRole ? ` · ${userRole}` : ''}{workerId ? ` · #${workerId}` : ''}</>}
        </Typography>
      </Box>
    </Box>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
 * Main page
 * ──────────────────────────────────────────────────────────────────────────── */
export default function AttendanceCorrectionRequests() {
  const currentUser = getCurrentUser();
  // Distinguish: user.id is the auth user PK; worker_id is the HR employee
  // record. Actions targeting the workflow always use worker_id; user.id
  // would silently misroute notifications and assignments.
  const workerId    = currentUser?.worker_id || currentUser?.workerId || currentUser?.WorkerId || null;
  const userRole    = (currentUser?.user_role || currentUser?.role || currentUser?.Role || '').toString();
  const workerName  = [
    currentUser?.firstname || currentUser?.FirstName || currentUser?.first_name || '',
    currentUser?.lastname  || currentUser?.LastName  || currentUser?.last_name  || '',
  ].filter(Boolean).join(' ').trim() || currentUser?.username || currentUser?.email || 'Punëtori';
  const userEmail   = currentUser?.email || '';
  const isManager   = isReviewer(currentUser);
  // If the logged-in user is not linked to a worker record, they can't be the
  // subject of a correction request. Reviewers can still triage others.
  const canCreateOwnRequest = !!workerId;

  const [requests, setRequests]         = useState([]);
  const [loading, setLoading]           = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchText, setSearchText]     = useState('');
  const [sortBy, setSortBy]             = useState('newest');
  const [selectedId, setSelectedId]     = useState(null);
  const [detail, setDetail]             = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [createMode, setCreateMode]     = useState(false);
  const [commentText, setCommentText]   = useState('');
  const [commentSending, setCommentSending] = useState(false);
  const [toast, setToast]               = useState({ open: false, msg: '', sev: 'success' });
  const [readMap, setReadMap]           = useState(() => loadReadMap(workerId));
  const [auditOpen, setAuditOpen]       = useState(false);
  const [auditRows, setAuditRows]       = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [assignOpen, setAssignOpen]     = useState(false);
  const [escalateOpen, setEscalateOpen] = useState(false);
  const [workersIndex, setWorkersIndex] = useState([]);

  const [form, setForm] = useState({
    type: 'correction', request_date: '',
    req_check_in_time: '', req_check_out_time: '',
    req_break_min: '', description: '', priority: 'normal',
  });

  const commentRef = useRef(null);

  const showToast = (msg, sev = 'success') => setToast({ open: true, msg, sev });

  const load = useCallback(async () => {
    setLoading(true);
    // Backend now scopes the query to the authenticated user automatically
    // for non-reviewers. We only pass worker_id when a reviewer wants to
    // narrow to a specific employee (not used yet — left for future filter).
    const r = await correctionService.list({ sort: sortBy });
    if (!r.ok) showToast(r.error || 'Gabim në ngarkimin e kërkesave', 'error');
    setRequests(r.data || []);
    setLoading(false);
  }, [sortBy]);

  useEffect(() => { load(); }, [load]);

  // Deep-link support: /attendance-corrections?create=1 opens the new-request
  // form directly (used by the Inbox "Kërkesë e re" menu).
  useEffect(() => {
    try {
      if (new URLSearchParams(window.location.search).get('create') === '1') {
        setCreateMode(true);
        setSelectedId(null);
        setDetail(null);
      }
    } catch { /* ignore */ }
  }, []);

  // Live polling — keeps the list feeling "real-time" without a websocket layer.
  useEffect(() => {
    const id = setInterval(() => { load(); if (detail?.id) refreshDetail(); }, 30000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load]);

  // Hydrate the index of workers once for the "Assign reviewer" picker.
  useEffect(() => {
    if (!isManager) return;
    apiJSON('/api/workers').then((r) => {
      if (r.ok) setWorkersIndex((r.data || []).filter(w => w?.id || w?.Id));
    });
  }, [isManager]);

  const markRead = useCallback((req) => {
    if (!req) return;
    const stamp = req.updated_at || req.last_event_at || req.created_at;
    setReadMap((prev) => {
      const next = { ...prev, [req.id]: stamp };
      saveReadMap(workerId, next);
      return next;
    });
  }, [workerId]);

  const openDetail = useCallback(async (req) => {
    if (!req) return;
    setSelectedId(req.id);
    setCreateMode(false);
    setDetailLoading(true);
    setCommentText('');
    markRead(req);
    const r = await correctionService.detail(req.id);
    if (!r.ok) showToast(r.error || 'Gabim në ngarkimin e kërkesës', 'error');
    setDetail(r.data || null);
    setDetailLoading(false);
  }, [markRead]);

  const refreshDetail = useCallback(async () => {
    if (!detail?.id) return;
    const r = await correctionService.detail(detail.id);
    if (r.ok) { setDetail(r.data); markRead(r.data); }
  }, [detail?.id, markRead]);

  // ── Workflow actions ──
  // Note: actor identity (reviewed_by/author_*) is NEVER sent from the
  // client — the backend fills it from the authenticated session so a
  // tampered request can't impersonate someone else.
  const changeStatus = async (newStatus, req = null) => {
    const target = req || detail;
    if (!target) return;
    const r = await correctionService.update(target.id, {
      status: newStatus,
      review_notes: (!req && commentText.trim()) ? commentText.trim() : undefined,
    });
    if (!r.ok) { showToast(r.error || 'Nuk u ndryshua statusi', 'error'); return; }
    if (!req && commentText.trim()) {
      await correctionService.comment(target.id, { body: commentText.trim() });
      setCommentText('');
    }
    showToast(`Statusi u ndryshua → ${STATUS_LABEL[newStatus] || newStatus}`);
    if (!req && detail) await refreshDetail();
    load();
  };

  const requestMoreInfo = async () => {
    if (!detail) return;
    const note = commentText.trim() || 'Ju lutem jepni më shumë detaje për këtë kërkesë.';
    const r = await correctionService.comment(detail.id, { body: note, request_info: true });
    if (!r.ok) { showToast(r.error || 'Gabim', 'error'); return; }
    setCommentText('');
    showToast('Sqarime u kërkuan — punëtori u njoftua');
    await refreshDetail();
    load();
  };

  const assignReviewer = async (assignedToId, assignedToName) => {
    if (!detail || !assignedToId) return;
    const r = await correctionService.assign(detail.id, {
      assigned_to: assignedToId, assigned_to_name: assignedToName || '',
    });
    if (!r.ok) { showToast(r.error || 'Gabim', 'error'); return; }
    showToast(`Caktuar te ${assignedToName || `#${assignedToId}`}`);
    await refreshDetail();
    load();
  };

  const escalateToHR = async (reason) => {
    if (!detail) return;
    const r = await correctionService.escalate(detail.id, { reason: reason || '' });
    if (!r.ok) { showToast(r.error || 'Gabim', 'error'); return; }
    showToast('U eskalua te HR');
    await refreshDetail();
    load();
  };

  const openAuditLog = async () => {
    if (!detail) return;
    setAuditOpen(true);
    setAuditLoading(true);
    const r = await correctionService.audit(detail.id);
    if (r.ok) setAuditRows(r.data || []);
    setAuditLoading(false);
  };

  const handleExport = () => {
    exportRequestsCSV(visibleRef.current || []);
    showToast(`Eksportuar ${(visibleRef.current || []).length} kërkesa CSV`);
  };

  const sendComment = async () => {
    if (!commentText.trim() || !detail) return;
    setCommentSending(true);
    // Direction + author are derived server-side from req.user so the worker
    // / reviewer distinction can't be spoofed.
    const r = await correctionService.comment(detail.id, {
      body: commentText.trim(),
    });
    if (r.ok) {
      setCommentText('');
      const hasMentions = MENTION_RE.test(commentText);
      MENTION_RE.lastIndex = 0;
      showToast(hasMentions ? 'Komenti u dërgua dhe përmendjet u njoftuan' : 'Komenti u dërgua');
      await refreshDetail();
      load();
    } else {
      showToast(r.error || 'Gabim gjatë dërgimit', 'error');
    }
    setCommentSending(false);
  };

  const startCreate = () => { setCreateMode(true); setSelectedId(null); setDetail(null); };
  const cancelCreate = () => {
    setCreateMode(false);
    setForm({ type: 'correction', request_date: '', req_check_in_time: '', req_check_out_time: '', req_break_min: '', description: '', priority: 'normal' });
  };

  const submitRequest = async () => {
    if (!form.request_date || !form.description.trim()) {
      showToast('Data dhe përshkrimi janë të detyrueshme', 'error'); return;
    }
    if (!workerId && !isManager) {
      showToast('Llogaria juaj nuk është e lidhur me një profil punëtori — kontaktoni HR', 'error');
      return;
    }
    // worker_id is optional: omit it and the server treats the logged-in user
    // as the owner. Reviewers may explicitly pass another worker_id to file
    // on behalf of someone else (UI hook not wired yet).
    const r = await correctionService.create({
      type: form.type, request_date: form.request_date,
      req_check_in:  buildIso(form.request_date, form.req_check_in_time),
      req_check_out: buildIso(form.request_date, form.req_check_out_time),
      req_break_min: form.req_break_min !== '' ? Number(form.req_break_min) : null,
      description: form.description.trim(),
      priority: form.priority || 'normal',
    });
    if (!r.ok) { showToast(r.error || 'Gabim', 'error'); return; }
    cancelCreate();
    showToast(`Kërkesa ${r.data.request_number} u dërgua — njoftimet u shpërndanë`);
    load();
    if (r.data?.id) openDetail(r.data);
  };

  const counts = useMemo(() =>
    requests.reduce((a, r) => { a[r.status] = (a[r.status] || 0) + 1; return a; }, {}),
  [requests]);

  // Unread = the request has new activity (updated_at) after the last time
  // the current user viewed its detail panel.
  const isUnread = useCallback((req) => {
    if (!req) return false;
    if (selectedId === req.id) return false;
    const stamp = req.updated_at || req.last_event_at || req.created_at;
    const seen  = readMap[req.id];
    if (!seen) return Boolean(stamp); // never opened
    return new Date(stamp).getTime() > new Date(seen).getTime();
  }, [readMap, selectedId]);

  const unreadTotal = useMemo(
    () => requests.reduce((n, r) => n + (isUnread(r) ? 1 : 0), 0),
    [requests, isUnread]
  );

  const q = searchText.trim().toLowerCase();
  const visible = useMemo(() => requests.filter((r) => {
    if (statusFilter !== 'all' && r.status !== statusFilter) return false;
    if (!q) return true;
    return (
      (r.request_number || '').toLowerCase().includes(q) ||
      (r.worker_name    || '').toLowerCase().includes(q) ||
      (r.description    || '').toLowerCase().includes(q) ||
      (r.assigned_to_name || '').toLowerCase().includes(q) ||
      (TYPE_LABEL[r.type] || r.type || '').toLowerCase().includes(q)
    );
  }), [requests, statusFilter, q]);

  // Keep latest filtered list available to non-state callers (e.g. CSV export).
  const visibleRef = useRef(visible);
  useEffect(() => { visibleRef.current = visible; }, [visible]);

  const hasFilters = !!(searchText || statusFilter !== 'all' || sortBy !== 'newest');
  const clearFilters = () => { setSearchText(''); setStatusFilter('all'); setSortBy('newest'); };

  const markAllRead = () => {
    const stamp = new Date().toISOString();
    const next = { ...readMap };
    requests.forEach((r) => { next[r.id] = stamp; });
    setReadMap(next);
    saveReadMap(workerId, next);
    showToast(`${unreadTotal} kërkesa u shënuan si të lexuara`);
  };

  const view = createMode ? 'create' : (selectedId ? 'detail' : 'empty');

  return (
    <Box sx={{
      bgcolor: T.surface,
      p: { xs: 1.25, md: 2 },
      display: { xs: 'block', lg: 'grid' },
      gridTemplateColumns: { lg: '360px 1fr' },
      gap: { lg: 1.5 },
      alignItems: 'start',
    }}>
      {/* ═══════════════════════ LEFT — INBOX ═══════════════════════ */}
      <Box sx={{
        display: { xs: view === 'empty' ? 'flex' : 'none', lg: 'flex' },
        flexDirection: 'column', gap: 1.25, minHeight: 0,
        position: { lg: 'sticky' },
        top: { lg: 16 },
        maxHeight: { lg: 'calc(100vh - 32px)' },
      }}>
        <Paper elevation={0} sx={{ ...cardShell, overflow: 'hidden', flexShrink: 0 }}>
          {/* Segmented control tabs (Linear/Notion style) */}
          <Box sx={{
            p: 0.75,
            borderBottom: `1px solid ${T.line}`,
            display: 'flex', gap: 0.25,
            overflowX: 'auto',
            '&::-webkit-scrollbar': { display: 'none' },
            scrollbarWidth: 'none',
          }}>
            {TAB_DEFS.map((t) => {
              const n = t.value === 'all' ? requests.length : (counts[t.value] || 0);
              const active = statusFilter === t.value;
              return (
                <Box
                  key={t.value}
                  onClick={() => setStatusFilter(t.value)}
                  sx={{
                    flexShrink: 0,
                    display: 'inline-flex', alignItems: 'center', gap: 0.625,
                    px: 1.25, py: 0.625,
                    borderRadius: '6px',
                    fontSize: 12.5, fontWeight: 600,
                    cursor: 'pointer', userSelect: 'none',
                    bgcolor: active ? T.brandLight : 'transparent',
                    color:   active ? T.brand      : T.ink500,
                    transition: 'background-color 120ms ease, color 120ms ease',
                    '&:hover': { bgcolor: active ? T.brandLight : T.surfaceAlt, color: active ? T.brand : T.ink },
                  }}
                >
                  {t.label}
                  <Box sx={{
                    minWidth: 18, height: 16, px: 0.5, borderRadius: '999px',
                    bgcolor: active ? T.white : T.surfaceAlt,
                    color:   active ? T.brand : T.ink400,
                    fontSize: 10.5, fontWeight: 700,
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  }}>{n}</Box>
                </Box>
              );
            })}
          </Box>

          <Box sx={{ p: 1, display: 'flex', gap: 0.625, alignItems: 'center' }}>
            <TextField
              size="small" fullWidth
              placeholder="Kërko numër, punëtor, përshkrim…"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  height: 34, borderRadius: '8px', bgcolor: T.surface, fontSize: 13,
                  transition: 'all 160ms ease',
                  '& fieldset': { borderColor: 'transparent' },
                  '&:hover': { bgcolor: T.surfaceAlt },
                  '&:hover fieldset': { borderColor: 'transparent' },
                  '&.Mui-focused': { bgcolor: T.white, boxShadow: `0 0 0 3px ${alpha(T.brand, 0.15)}` },
                  '&.Mui-focused fieldset': { borderColor: T.brand, borderWidth: 1 },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 16, color: T.ink400 }} />
                  </InputAdornment>
                ),
                endAdornment: searchText ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchText('')} sx={{ p: 0.25 }}>
                      <CloseRoundedIcon sx={{ fontSize: 14, color: T.ink400 }} />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />
            <SortMenu value={sortBy} onChange={setSortBy} />
            <Tooltip title="Rifresko" arrow placement="top">
              <IconButton onClick={load} sx={{
                width: 34, height: 34, borderRadius: '8px', flexShrink: 0,
                color: T.ink500,
                '&:hover': { bgcolor: T.surfaceAlt, color: T.ink },
              }}>
                <RefreshRoundedIcon sx={{ fontSize: 17 }} />
              </IconButton>
            </Tooltip>
            <Tooltip
              title={canCreateOwnRequest || isManager
                ? 'Kërkesë e re'
                : 'Llogaria juaj nuk është e lidhur me një profil punëtori'}
              arrow placement="top"
            >
              <span>
                <IconButton
                  onClick={startCreate}
                  disabled={!canCreateOwnRequest && !isManager}
                  sx={{
                    width: 34, height: 34, borderRadius: '8px', flexShrink: 0,
                    bgcolor: T.brand, color: T.white,
                    '&:hover': { bgcolor: T.brandHover },
                    '&.Mui-disabled': { bgcolor: T.surfaceAlt, color: T.ink400 },
                  }}
                >
                  <AddIcon sx={{ fontSize: 17 }} />
                </IconButton>
              </span>
            </Tooltip>
          </Box>

          {/* Acting-as bar — makes it obvious which authenticated identity will
              be recorded on every action below. Workers without a worker_id
              linkage see a clear warning instead. */}
          <ActingAsBar
            workerName={workerName}
            userEmail={userEmail}
            userRole={userRole}
            workerId={workerId}
            isManager={isManager}
            canCreateOwnRequest={canCreateOwnRequest}
          />
          {unreadTotal > 0 && (
            <Box
              sx={{
                px: 1.25, py: 0.625,
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                borderTop: `1px solid ${T.lineSoft}`,
                bgcolor: alpha(T.brand, 0.04),
              }}
            >
              <Typography sx={{ fontSize: 11.5, color: T.brand, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                <NotificationsActiveRoundedIcon sx={{ fontSize: 13 }} />
                {unreadTotal} të reja
              </Typography>
              <Button
                onClick={markAllRead}
                size="small"
                sx={{
                  height: 22, borderRadius: '6px', px: 0.875, minWidth: 0,
                  textTransform: 'none', fontSize: 11.5, color: T.brand, fontWeight: 600,
                  '&:hover': { bgcolor: alpha(T.brand, 0.08) },
                }}
              >Shëno të gjitha</Button>
            </Box>
          )}
        </Paper>

        {/* Queue */}
        <Paper elevation={0} sx={{
          ...cardShell, overflow: 'hidden',
          flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column',
        }}>
          {hasFilters && (
            <Box sx={{
              px: 1.5, py: 0.75,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              borderBottom: `1px solid ${T.line}`, bgcolor: T.surface,
            }}>
              <Typography sx={{ fontSize: 11.5, color: T.ink500 }}>
                {visible.length} prej {requests.length}
              </Typography>
              <Button size="small" onClick={clearFilters}
                sx={{
                  height: 22, borderRadius: '6px', px: 0.875, minWidth: 0,
                  textTransform: 'none', fontSize: 11.5, color: T.ink500,
                  '&:hover': { bgcolor: T.surfaceAlt, color: T.ink },
                }}
              >Pastro</Button>
            </Box>
          )}

          <Box sx={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
            {loading ? (
              [...Array(7)].map((_, i) => (
                <Box key={i} sx={{
                  px: 1.5, py: 1.25,
                  display: 'flex', alignItems: 'center', gap: 1.25,
                  borderBottom: `1px solid ${T.lineSoft}`,
                }}>
                  <Skeleton variant="circular" width={32} height={32} />
                  <Box sx={{ flex: 1 }}>
                    <Skeleton width="65%" height={12} />
                    <Skeleton width="85%" height={10} sx={{ mt: 0.5 }} />
                  </Box>
                </Box>
              ))
            ) : visible.length === 0 ? (
              <Stack alignItems="center" spacing={1.25} sx={{ py: 5, px: 3 }}>
                <Box sx={{
                  width: 44, height: 44, borderRadius: '12px',
                  bgcolor: T.brandLight, color: T.brand,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <InboxRoundedIcon sx={{ fontSize: 22 }} />
                </Box>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: T.ink, textAlign: 'center' }}>
                  {hasFilters ? 'Asnjë rezultat' : 'Asnjë kërkesë'}
                </Typography>
                <Typography sx={{ fontSize: 12, color: T.ink500, textAlign: 'center', maxWidth: 260 }}>
                  {hasFilters
                    ? 'Provo me një term tjetër ose pastro filtrat.'
                    : isManager
                      ? 'Kërkesat e reja do të shfaqen këtu.'
                      : 'Krijo një kërkesë për korrigjim prezence.'}
                </Typography>
              </Stack>
            ) : (
              visible.map((r, idx) => (
                <QueueRow
                  key={r.id}
                  r={r}
                  selected={selectedId === r.id}
                  unread={isUnread(r)}
                  onClick={() => openDetail(r)}
                  divider={idx < visible.length - 1}
                />
              ))
            )}
          </Box>
        </Paper>
      </Box>

      {/* ═══════════════════════ RIGHT — APPROVAL WORKSPACE ═══════════════════════ */}
      <ApprovalWorkspace
        view={view}
        loading={detailLoading}
        detail={detail}
        isManager={isManager}
        onCloseDetail={() => { setSelectedId(null); setDetail(null); }}
        onApprove={() => changeStatus('approved')}
        onReject={() => changeStatus('rejected')}
        onReopen={() => changeStatus('open')}
        onRequestInfo={requestMoreInfo}
        onAssign={() => setAssignOpen(true)}
        onEscalate={() => setEscalateOpen(true)}
        onAudit={openAuditLog}
        onExport={handleExport}
        commentText={commentText}
        setCommentText={setCommentText}
        commentSending={commentSending}
        commentRef={commentRef}
        onSendComment={sendComment}
        form={form}
        setForm={setForm}
        onCancelCreate={cancelCreate}
        onSubmit={submitRequest}
        workerName={workerName}
        onStartCreate={startCreate}
      />

      {/* ── Assign reviewer dialog ── */}
      <AssignDialog
        open={assignOpen}
        workers={workersIndex}
        onClose={() => setAssignOpen(false)}
        onAssign={(id, name) => { assignReviewer(id, name); setAssignOpen(false); }}
      />

      {/* ── Escalate to HR dialog ── */}
      <EscalateDialog
        open={escalateOpen}
        onClose={() => setEscalateOpen(false)}
        onConfirm={(reason) => { escalateToHR(reason); setEscalateOpen(false); }}
      />

      {/* ── Audit log drawer ── */}
      <AuditLogDialog
        open={auditOpen}
        loading={auditLoading}
        rows={auditRows}
        onClose={() => setAuditOpen(false)}
      />

      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast(p => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={toast.sev} variant="filled"
          onClose={() => setToast(p => ({ ...p, open: false }))}
          sx={{
            fontSize: 13, fontWeight: 500,
            borderRadius: '10px',
            boxShadow: '0 10px 24px rgba(15,23,42,.12)',
            alignItems: 'center',
          }}
        >
          {toast.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
 * Queue row — compact ticket
 * ──────────────────────────────────────────────────────────────────────────── */
function QueueRow({ r, selected, unread, onClick, divider }) {
  const tone = TONE[STATUS_TONE[r.status]] || TONE.neutral;
  const priorityCfg = PRIORITY_OPTS.find(p => p.value === r.priority) || PRIORITY_OPTS[1];
  const showPriorityDot = r.priority === 'high' || r.priority === 'urgent';
  return (
    <Box
      role="button"
      onClick={onClick}
      sx={{
        position: 'relative',
        px: 1.5, py: 1,
        display: 'flex', alignItems: 'flex-start', gap: 1.25,
        cursor: 'pointer',
        borderLeft: '2px solid',
        borderLeftColor: selected ? T.brand : unread ? T.warning : 'transparent',
        bgcolor: selected ? T.brandSoft : 'transparent',
        borderBottom: divider ? `1px solid ${T.lineSoft}` : 0,
        transition: 'all 140ms ease',
        '&:hover': { bgcolor: selected ? T.brandSoft : T.surface },
      }}
    >
      <Box sx={{ position: 'relative', flexShrink: 0 }}>
        <Avatar name={r.worker_name} size={32} />
        <Box sx={{
          position: 'absolute', bottom: -2, right: -2,
          width: 12, height: 12, borderRadius: '50%',
          bgcolor: tone.dot,
          border: `2px solid ${selected ? T.brandSoft : T.white}`,
        }} />
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
          <Stack direction="row" alignItems="center" spacing={0.625} sx={{ minWidth: 0 }}>
            {unread && (
              <Box
                sx={{
                  width: 6, height: 6, borderRadius: '50%',
                  bgcolor: T.brand, flexShrink: 0,
                }}
                aria-label="i palexuar"
              />
            )}
            <Typography noWrap sx={{
              fontSize: 13, fontWeight: unread ? 700 : 600, color: T.ink, lineHeight: 1.3,
            }}>
              {r.worker_name || 'Punëtor'}
            </Typography>
            {showPriorityDot && (
              <Tooltip title={`Prioriteti: ${priorityCfg.label}`} arrow>
                <PriorityHighRoundedIcon sx={{ fontSize: 13, color: priorityCfg.dot }} />
              </Tooltip>
            )}
            {r.escalated_to_hr && (
              <Tooltip title="Eskaluar te HR" arrow>
                <EscalatorWarningRoundedIcon sx={{ fontSize: 13, color: T.errorInk }} />
              </Tooltip>
            )}
          </Stack>
          <Typography sx={{
            fontSize: 11, color: T.ink400, flexShrink: 0,
            fontVariantNumeric: 'tabular-nums',
          }}>
            {relativeFromNow(r.last_event_at || r.updated_at || r.created_at)}
          </Typography>
        </Stack>

        <Typography noWrap sx={{ fontSize: 11.5, color: T.ink500, mt: 0.25, lineHeight: 1.4 }}>
          <Box component="span" sx={{ color: T.ink700, fontWeight: 500 }}>
            {TYPE_LABEL[r.type] || r.type}
          </Box>
          <Box component="span" sx={{ mx: 0.5, color: T.ink300 }}>·</Box>
          {fmtDateOnly(r.request_date)}
          {r.assigned_to_name && (
            <>
              <Box component="span" sx={{ mx: 0.5, color: T.ink300 }}>·</Box>
              <Box component="span" sx={{ color: T.brand, fontWeight: 600 }}>→ {r.assigned_to_name}</Box>
            </>
          )}
        </Typography>

        {r.description && (
          <Typography noWrap sx={{
            fontSize: 11.5, color: T.ink400, mt: 0.25, lineHeight: 1.4,
          }}>
            {r.description}
          </Typography>
        )}

        {/* Activity count (comments) */}
        {(r.comments_count > 0 || (r.comments && r.comments.length > 0)) && (
          <Stack direction="row" alignItems="center" spacing={0.375} sx={{ mt: 0.5 }}>
            <Box sx={{
              fontSize: 10.5, color: T.ink400, fontWeight: 600,
              display: 'inline-flex', alignItems: 'center', gap: 0.25,
            }}>
              <Box component="span" sx={{ fontSize: 11 }}>💬</Box>
              {r.comments_count || (r.comments || []).length}
            </Box>
          </Stack>
        )}
      </Box>
    </Box>
  );
}

function SortMenu({ value, onChange }) {
  const [anchor, setAnchor] = useState(null);
  const current = SORT_OPTS.find(s => s.value === value) || SORT_OPTS[0];
  return (
    <>
      <Tooltip title={`Rendi: ${current.label}`} arrow placement="top">
        <IconButton
          onClick={(e) => setAnchor(e.currentTarget)}
          sx={{
            width: 34, height: 34, borderRadius: '8px', flexShrink: 0,
            color: T.ink500,
            '&:hover': { bgcolor: T.surfaceAlt, color: T.ink },
          }}
        >
          <SwapVertRoundedIcon sx={{ fontSize: 17 }} />
        </IconButton>
      </Tooltip>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
        {SORT_OPTS.map((s) => (
          <MenuItem
            key={s.value}
            selected={s.value === value}
            onClick={() => { onChange(s.value); setAnchor(null); }}
            sx={{ fontSize: 13, minWidth: 180 }}
          >
            {s.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
 * ApprovalWorkspace — the reusable shell
 * Layout: sticky paper with internal flex-column. Header / Body (scrolls,
 * ambient surface) / Footer (action bar + composer). On mobile collapses to
 * natural flow.
 * ──────────────────────────────────────────────────────────────────────────── */
function ApprovalWorkspace({
  view, loading, detail, isManager,
  onCloseDetail, onApprove, onReject, onReopen,
  onRequestInfo, onAssign, onEscalate, onAudit, onExport,
  commentText, setCommentText, commentSending, commentRef, onSendComment,
  form, setForm, onCancelCreate, onSubmit, workerName, onStartCreate,
}) {
  return (
    <Paper elevation={0} sx={{
      ...cardShell,
      display: { xs: view === 'empty' ? 'none' : 'flex', lg: 'flex' },
      flexDirection: 'column',
      overflow: 'hidden',
      // Auto-size to content. Sticky positioning preserved for desktop scroll
      // behavior, but no forced viewport height — workspace grows naturally.
      position: { lg: 'sticky' },
      top: { lg: 16 },
    }}>
      {view === 'empty' && <WorkspaceEmpty isManager={isManager} onCreate={onStartCreate} />}

      {view === 'detail' && (
        <WorkspaceDetail
          loading={loading}
          detail={detail}
          isManager={isManager}
          commentText={commentText}
          setCommentText={setCommentText}
          commentSending={commentSending}
          commentRef={commentRef}
          onClose={onCloseDetail}
          onApprove={onApprove}
          onReject={onReject}
          onReopen={onReopen}
          onRequestInfo={onRequestInfo}
          onAssign={onAssign}
          onEscalate={onEscalate}
          onAudit={onAudit}
          onExport={onExport}
          onSendComment={onSendComment}
        />
      )}

      {view === 'create' && (
        <WorkspaceCreate
          form={form}
          setForm={setForm}
          onCancel={onCancelCreate}
          onSubmit={onSubmit}
          workerName={workerName}
        />
      )}
    </Paper>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
 * Workspace: Empty state
 * ──────────────────────────────────────────────────────────────────────────── */
function WorkspaceEmpty({ isManager, onCreate }) {
  return (
    <Box sx={{
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      px: 3, py: 4,
    }}>
      <Box sx={{ textAlign: 'center', maxWidth: 320 }}>
        <Box sx={{
          width: 48, height: 48, borderRadius: '12px',
          bgcolor: T.brandLight, color: T.brand,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          mb: 1.5,
        }}>
          <WorkspacesOutlinedIcon sx={{ fontSize: 24 }} />
        </Box>
        <Typography sx={{ fontSize: 14.5, fontWeight: 600, color: T.ink, lineHeight: 1.3 }}>
          Zgjidh një kërkesë
        </Typography>
        <Typography sx={{ fontSize: 12.5, color: T.ink500, mt: 0.5, lineHeight: 1.5 }}>
          {isManager
            ? 'Klikon majtas për të parë detajet dhe për të aprovuar ose refuzuar.'
            : 'Zgjidh nga lista ose krijo një kërkesë të re.'}
        </Typography>
        <Button
          onClick={onCreate}
          startIcon={<AddIcon sx={{ fontSize: 15 }} />}
          disableElevation
          sx={{
            mt: 1.75, height: 32, px: 1.5, borderRadius: '8px',
            bgcolor: T.brand, color: T.white,
            textTransform: 'none', fontSize: 12.5, fontWeight: 600,
            '&:hover': { bgcolor: T.brandHover },
          }}
        >Kërkesë e re</Button>
      </Box>
    </Box>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
 * Workspace: Detail
 * ──────────────────────────────────────────────────────────────────────────── */
function WorkspaceDetail({
  loading, detail, isManager,
  commentText, setCommentText, commentSending, commentRef,
  onClose, onApprove, onReject, onReopen, onSendComment,
  onRequestInfo, onAssign, onEscalate, onAudit, onExport,
}) {
  const composerInputRef = React.useRef(null);
  const [headerMenu, setHeaderMenu] = React.useState(null);

  // Focus the composer so the reviewer can type the clarification message.
  const focusComposer = () => {
    if (commentRef?.current) commentRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
    setTimeout(() => composerInputRef.current?.focus(), 220);
  };

  const handleRequestInfo = () => {
    // If the composer is empty, focus it instead of immediately firing the
    // request — gives the reviewer a chance to type a custom question.
    if (!commentText.trim()) { focusComposer(); return; }
    onRequestInfo?.();
  };

  const handleCopyRequestNumber = () => {
    if (detail?.request_number && navigator.clipboard) {
      navigator.clipboard.writeText(detail.request_number);
    }
    setHeaderMenu(null);
  };

  const mention = (text) => {
    setCommentText((prev) => `${prev}${prev && !prev.endsWith(' ') ? ' ' : ''}${text} `);
    setTimeout(() => composerInputRef.current?.focus(), 60);
  };

  if (loading || !detail) {
    return (
      <Box sx={{ p: 2.5 }}>
        <Skeleton variant="rounded" width="55%" height={20} />
        <Skeleton variant="rounded" width="35%" height={16} sx={{ mt: 1 }} />
        <Skeleton variant="rounded" height={120} sx={{ mt: 2 }} />
        <Skeleton variant="rounded" height={80} sx={{ mt: 1.5 }} />
      </Box>
    );
  }

  const isFinal = ['approved', 'rejected'].includes(detail.status);
  const comments = detail.comments || [];

  return (
    <>
      {/* ── Header (sticky top of panel) ── */}
      <Box sx={{
        px: { xs: 1.75, md: 2.5 }, py: 1.375,
        borderBottom: `1px solid ${T.line}`,
        bgcolor: T.white,
        flexShrink: 0,
      }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1.5}>
          <Stack direction="row" spacing={1.5} sx={{ minWidth: 0, flex: 1 }}>
            <IconButton
              onClick={onClose}
              sx={{
                display: { xs: 'inline-flex', lg: 'none' },
                width: 32, height: 32, borderRadius: '8px',
                color: T.ink500, mt: 0.5,
                '&:hover': { bgcolor: T.surfaceAlt },
              }}
            ><ArrowBackRoundedIcon sx={{ fontSize: 17 }} /></IconButton>

            <Avatar name={detail.worker_name} size={40} />

            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.25 }}>
                <Typography sx={{
                  fontSize: 11, fontWeight: 600, color: T.ink400,
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                  letterSpacing: 0.2,
                }}>
                  {detail.request_number}
                </Typography>
                <Box sx={{ width: 3, height: 3, borderRadius: '50%', bgcolor: T.ink300 }} />
                <Typography sx={{ fontSize: 11.5, color: T.ink500 }}>
                  {TYPE_LABEL[detail.type] || detail.type}
                </Typography>
              </Stack>
              <Typography sx={{ fontSize: 16, fontWeight: 700, color: T.ink, lineHeight: 1.25 }} noWrap>
                {detail.worker_name || 'Punëtor'}
              </Typography>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.5 }}>
                <StatusBadge status={detail.status} size="lg" />
                <Typography sx={{ fontSize: 11.5, color: T.ink400 }}>
                  Dërguar {fmtDate(detail.created_at)}
                </Typography>
              </Stack>
            </Box>
          </Stack>

          <Stack direction="row" spacing={0.25} sx={{ display: { xs: 'none', lg: 'inline-flex' }, flexShrink: 0 }}>
            <Tooltip title="Veprime" arrow>
              <IconButton
                onClick={(e) => setHeaderMenu(e.currentTarget)}
                sx={{
                  width: 32, height: 32, borderRadius: '8px',
                  color: T.ink500,
                  '&:hover': { bgcolor: T.surfaceAlt, color: T.ink },
                }}
              >
                <MoreHorizRoundedIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Mbyll" arrow>
              <IconButton
                onClick={onClose}
                sx={{
                  width: 32, height: 32, borderRadius: '8px',
                  color: T.ink500,
                  '&:hover': { bgcolor: T.surfaceAlt, color: T.ink },
                }}
              ><CloseRoundedIcon sx={{ fontSize: 17 }} /></IconButton>
            </Tooltip>
          </Stack>
        </Stack>

        {/* Actions menu (full workflow) */}
        <Menu
          anchorEl={headerMenu}
          open={Boolean(headerMenu)}
          onClose={() => setHeaderMenu(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          PaperProps={{ sx: { minWidth: 220 } }}
        >
          {isManager && !['approved','rejected'].includes(detail.status) && (
            <MenuItem onClick={() => { setHeaderMenu(null); onApprove?.(); }} sx={{ fontSize: 13 }}>
              <ListItemIcon><CheckRoundedIcon fontSize="small" sx={{ color: T.successInk }} /></ListItemIcon>
              <ListItemText primary="Aprovo" primaryTypographyProps={{ fontSize: 13, fontWeight: 600 }} />
            </MenuItem>
          )}
          {isManager && !['approved','rejected'].includes(detail.status) && (
            <MenuItem onClick={() => { setHeaderMenu(null); onReject?.(); }} sx={{ fontSize: 13 }}>
              <ListItemIcon><BlockRoundedIcon fontSize="small" sx={{ color: T.errorInk }} /></ListItemIcon>
              <ListItemText primary="Refuzo" primaryTypographyProps={{ fontSize: 13, fontWeight: 600 }} />
            </MenuItem>
          )}
          {isManager && !['approved','rejected'].includes(detail.status) && (
            <MenuItem onClick={() => { setHeaderMenu(null); handleRequestInfo(); }} sx={{ fontSize: 13 }}>
              <ListItemIcon><HelpOutlineRoundedIcon fontSize="small" sx={{ color: '#A855F7' }} /></ListItemIcon>
              <ListItemText primary="Kërko sqarime" primaryTypographyProps={{ fontSize: 13 }} />
            </MenuItem>
          )}
          {isManager && <Divider sx={{ my: 0.5 }} />}
          {isManager && (
            <MenuItem onClick={() => { setHeaderMenu(null); onAssign?.(); }} sx={{ fontSize: 13 }}>
              <ListItemIcon><AssignmentIndRoundedIcon fontSize="small" sx={{ color: T.ink500 }} /></ListItemIcon>
              <ListItemText
                primary="Cakto shqyrtues"
                secondary={detail.assigned_to_name || null}
                primaryTypographyProps={{ fontSize: 13 }}
                secondaryTypographyProps={{ fontSize: 11 }}
              />
            </MenuItem>
          )}
          {isManager && !detail.escalated_to_hr && (
            <MenuItem onClick={() => { setHeaderMenu(null); onEscalate?.(); }} sx={{ fontSize: 13 }}>
              <ListItemIcon><EscalatorWarningRoundedIcon fontSize="small" sx={{ color: T.errorInk }} /></ListItemIcon>
              <ListItemText primary="Eskaloji te HR" primaryTypographyProps={{ fontSize: 13 }} />
            </MenuItem>
          )}
          <Divider sx={{ my: 0.5 }} />
          <MenuItem onClick={() => { setHeaderMenu(null); onAudit?.(); }} sx={{ fontSize: 13 }}>
            <ListItemIcon><HistoryRoundedIcon fontSize="small" sx={{ color: T.ink500 }} /></ListItemIcon>
            <ListItemText primary="Shiko regjistrin e auditimit" primaryTypographyProps={{ fontSize: 13 }} />
          </MenuItem>
          <MenuItem onClick={() => { setHeaderMenu(null); onExport?.(); }} sx={{ fontSize: 13 }}>
            <ListItemIcon><DownloadRoundedIcon fontSize="small" sx={{ color: T.ink500 }} /></ListItemIcon>
            <ListItemText primary="Eksporto si CSV" primaryTypographyProps={{ fontSize: 13 }} />
          </MenuItem>
          <Divider sx={{ my: 0.5 }} />
          <MenuItem onClick={handleCopyRequestNumber} sx={{ fontSize: 13 }}>
            <ListItemIcon><Box sx={{ width: 18, fontSize: 14, textAlign: 'center', color: T.ink500 }}>#</Box></ListItemIcon>
            <ListItemText primary="Kopjo numrin e kërkesës" primaryTypographyProps={{ fontSize: 13 }} />
          </MenuItem>
        </Menu>
      </Box>

      {/* ── Body (sizes to content; scrolls only when overflowing panel max) ── */}
      <Box sx={{
        minHeight: 0,
        overflowY: 'auto',
        px: { xs: 1.75, md: 2.5 },
        py: 1.5,
      }}>
        {/* — Overview — */}
        <SectionHeading>Përmbledhje</SectionHeading>
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
          gap: { xs: 1, sm: 1.5 },
          mb: 0.5,
        }}>
          <MetaRow label="Punëtori" value={detail.worker_name} />
          <MetaRow label="Lloji" value={TYPE_LABEL[detail.type] || detail.type} />
          <MetaRow label="Data e kërkuar" value={fmtDateOnly(detail.request_date)} />
          <MetaRow label="Dërguar" value={fmtDate(detail.created_at)} />
          {detail.reviewed_by_name && (
            <MetaRow label="Shqyrtuar nga" value={detail.reviewed_by_name} />
          )}
        </Box>

        <SoftDivider />

        {/* — Requested correction (OLD → NEW comparison) — */}
        {(detail.req_check_in || detail.req_check_out || detail.req_break_min != null) && (
          <>
            <SectionHeading>Korrigjimi i kërkuar</SectionHeading>
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
              gap: 1,
              mb: 0.5,
            }}>
              {detail.req_check_in && (
                <ComparisonTile
                  icon={LoginRoundedIcon} tone="success" label="Check-in"
                  oldValue={detail.original_check_in ? fmtTime(detail.original_check_in) : null}
                  newValue={fmtTime(detail.req_check_in)}
                />
              )}
              {detail.req_check_out && (
                <ComparisonTile
                  icon={LogoutRoundedIcon} tone="warning" label="Check-out"
                  oldValue={detail.original_check_out ? fmtTime(detail.original_check_out) : null}
                  newValue={fmtTime(detail.req_check_out)}
                />
              )}
              {detail.req_break_min != null && (
                <ComparisonTile
                  icon={TimelapseRoundedIcon} tone="info" label="Pauzë"
                  oldValue={detail.original_break_min != null ? `${detail.original_break_min} min` : null}
                  newValue={`${detail.req_break_min} min`}
                />
              )}
            </Box>
            <SoftDivider />
          </>
        )}

        {/* — Reason — */}
        {detail.description && (
          <>
            <SectionHeading>Arsyeja</SectionHeading>
            <Typography sx={{
              fontSize: 13.5, color: T.ink700,
              whiteSpace: 'pre-wrap', lineHeight: 1.6,
            }}>
              {detail.description}
            </Typography>
            <SoftDivider />
          </>
        )}

        {/* — Approval workflow stepper (4 stages) — */}
        <SectionHeading>Rrjedha e aprovimit</SectionHeading>
        <WorkflowStepper detail={detail} />
        <SoftDivider />

        {/* — Activity Timeline (events + discussion, unified) — */}
        <SectionHeading count={comments.length}>Aktiviteti</SectionHeading>
        <ActivityTimeline detail={detail} comments={comments} />
      </Box>

      {/* ── Sticky footer: Approval action bar + composer ─────────────────── */}
      <Box
        ref={commentRef}
        sx={{
          // Stays pinned to bottom of viewport while the workspace body scrolls.
          position: 'sticky',
          bottom: 0,
          zIndex: 2,
          flexShrink: 0,
          borderTop: `1px solid ${T.line}`,
          bgcolor: 'rgba(255,255,255,0.92)',
          backdropFilter: 'saturate(180%) blur(8px)',
          WebkitBackdropFilter: 'saturate(180%) blur(8px)',
        }}
      >
        {isManager && (
          <Box sx={{
            px: { xs: 1.75, md: 2.5 }, py: 1.25,
            display: 'flex', gap: 0.75, alignItems: 'center',
            borderBottom: `1px solid ${T.lineSoft}`,
          }}>
            {!isFinal ? (
              <>
                {/* Destructive (left) */}
                <Button
                  onClick={onReject}
                  startIcon={<BlockRoundedIcon sx={{ fontSize: 15 }} />}
                  sx={{
                    height: 34, px: 1.5, borderRadius: '8px',
                    bgcolor: T.white, color: T.errorInk,
                    border: `1px solid ${alpha(T.error, 0.3)}`,
                    textTransform: 'none', fontSize: 12.5, fontWeight: 600,
                    '&:hover': { bgcolor: T.errorBg, borderColor: T.error },
                  }}
                >Refuzo</Button>
                {/* Secondary (middle) — request more info */}
                <Button
                  onClick={handleRequestInfo}
                  startIcon={<HelpOutlineRoundedIcon sx={{ fontSize: 15 }} />}
                  sx={{
                    height: 34, px: 1.5, borderRadius: '8px',
                    bgcolor: T.white, color: '#6B21A8',
                    border: `1px solid ${alpha('#A855F7', 0.35)}`,
                    textTransform: 'none', fontSize: 12.5, fontWeight: 600,
                    '&:hover': { bgcolor: '#F3E8FF', borderColor: '#A855F7' },
                  }}
                >Kërko sqarime</Button>
                {/* Tertiary — assign reviewer */}
                <Tooltip title="Cakto shqyrtues" arrow>
                  <IconButton
                    onClick={onAssign}
                    sx={{
                      width: 34, height: 34, borderRadius: '8px',
                      color: T.ink500,
                      border: `1px solid ${T.line}`,
                      '&:hover': { bgcolor: T.surface, borderColor: T.ink300, color: T.ink },
                    }}
                  >
                    <AssignmentIndRoundedIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
                {/* Escalate */}
                {!detail.escalated_to_hr && (
                  <Tooltip title="Eskaloji te HR" arrow>
                    <IconButton
                      onClick={onEscalate}
                      sx={{
                        width: 34, height: 34, borderRadius: '8px',
                        color: T.errorInk,
                        border: `1px solid ${alpha(T.error, 0.25)}`,
                        '&:hover': { bgcolor: T.errorBg, borderColor: T.error },
                      }}
                    >
                      <EscalatorWarningRoundedIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                )}
                <Box sx={{ flex: 1 }} />
                {/* Primary (right) */}
                <Button
                  onClick={onApprove}
                  startIcon={<CheckRoundedIcon sx={{ fontSize: 15 }} />}
                  disableElevation
                  sx={{
                    height: 34, px: 1.75, borderRadius: '8px',
                    bgcolor: T.successInk, color: T.white,
                    textTransform: 'none', fontSize: 12.5, fontWeight: 600,
                    boxShadow: `0 1px 2px ${alpha(T.success, 0.25)}`,
                    '&:hover': { bgcolor: T.success },
                  }}
                >Aprovo</Button>
              </>
            ) : (
              <>
                <GavelRoundedIcon sx={{ fontSize: 15, color: T.ink400 }} />
                <Typography sx={{
                  fontSize: 12, color: T.ink500, fontWeight: 500,
                  textTransform: 'none', mr: 'auto',
                }}>
                  Kjo kërkesë është {detail.status === 'approved' ? 'aprovuar' : 'refuzuar'}.
                </Typography>
                <Button
                  onClick={onReopen}
                  startIcon={<LockOpenRoundedIcon sx={{ fontSize: 15 }} />}
                  sx={{
                    height: 34, px: 1.5, borderRadius: '8px',
                    bgcolor: T.white, color: T.warningInk,
                    border: `1px solid ${alpha(T.warning, 0.35)}`,
                    textTransform: 'none', fontSize: 12.5, fontWeight: 600,
                    '&:hover': { bgcolor: T.warningBg, borderColor: T.warning },
                  }}
                >Rihap kërkesën</Button>
              </>
            )}
          </Box>
        )}

        {detail.status !== 'rejected' && (
          <Box sx={{ px: { xs: 1.75, md: 2.5 }, py: 1.25 }}>
            <Stack direction="row" spacing={1} alignItems="flex-end">
              <TextField
                inputRef={composerInputRef}
                fullWidth multiline
                minRows={1} maxRows={4}
                placeholder={isManager ? 'Shkruaj koment, kërko sqarime ose jep arsye…' : 'Shtoni informacione shtesë…'}
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                onKeyDown={(e) => {
                  if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') { e.preventDefault(); onSendComment(); }
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '10px', bgcolor: T.surface,
                    fontSize: 13, lineHeight: 1.5, py: 0.875, px: 1.25,
                    '& fieldset': { borderColor: 'transparent' },
                    '&:hover fieldset': { borderColor: T.ink300 },
                    '&.Mui-focused fieldset': { borderColor: T.brand, borderWidth: 1 },
                    '&.Mui-focused': { bgcolor: T.white, boxShadow: `0 0 0 3px ${alpha(T.brand, 0.12)}` },
                  },
                }}
              />
              <Tooltip title="Përmend (@) — njofto dikë" arrow placement="top">
                <IconButton
                  onClick={() => mention('@')}
                  sx={{
                    width: 34, height: 34, borderRadius: '8px',
                    color: T.ink500, flexShrink: 0,
                    '&:hover': { bgcolor: T.surfaceAlt, color: T.brand },
                  }}
                >
                  <AlternateEmailRoundedIcon sx={{ fontSize: 17 }} />
                </IconButton>
              </Tooltip>
              <Button
                onClick={onSendComment}
                disabled={!commentText.trim() || commentSending}
                disableElevation
                sx={{
                  height: 34, px: 1.5, borderRadius: '8px', minWidth: 0,
                  bgcolor: T.brand, color: T.white,
                  textTransform: 'none', fontSize: 13, fontWeight: 600,
                  flexShrink: 0,
                  '&:hover': { bgcolor: T.brandHover },
                  '&.Mui-disabled': { bgcolor: T.surfaceAlt, color: T.ink400 },
                }}
              >
                {commentSending
                  ? <CircularProgress size={14} sx={{ color: 'inherit' }} />
                  : <SendRoundedIcon sx={{ fontSize: 16 }} />}
              </Button>
            </Stack>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.5 }}>
              <Typography sx={{ fontSize: 10.5, color: T.ink400 }}>
                ⌘/Ctrl + Enter për të dërguar
              </Typography>
              <Box sx={{ width: 2, height: 2, borderRadius: '50%', bgcolor: T.ink300 }} />
              <Typography sx={{ fontSize: 10.5, color: T.ink400 }}>
                Përdor <Box component="span" sx={{ fontFamily: 'ui-monospace, monospace', color: T.brand, fontWeight: 600 }}>@email</Box> ose <Box component="span" sx={{ fontFamily: 'ui-monospace, monospace', color: T.brand, fontWeight: 600 }}>@emer.mbiemer</Box> për të njoftuar dikë
              </Typography>
              <Box sx={{ flex: 1 }} />
              <Stack direction="row" alignItems="center" spacing={0.5} sx={{ color: T.ink400 }}>
                <EmailRoundedIcon sx={{ fontSize: 11 }} />
                <Typography sx={{ fontSize: 10.5 }}>email</Typography>
                <Box sx={{ width: 2, height: 2, borderRadius: '50%', bgcolor: T.ink300 }} />
                <NotificationsActiveRoundedIcon sx={{ fontSize: 11 }} />
                <Typography sx={{ fontSize: 10.5 }}>Slack</Typography>
              </Stack>
            </Stack>
          </Box>
        )}
      </Box>
    </>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
 * Activity Timeline — unified events + comments (Jira/Linear style)
 * ──────────────────────────────────────────────────────────────────────────── */
function ActivityTimeline({ detail, comments }) {
  const events = [];

  // Submission marker — derived from the request row itself.
  events.push({
    kind: 'system',
    icon: FlagRoundedIcon,
    tone: 'info',
    actor: detail.worker_name,
    title: 'dërgoi kërkesën',
    at: detail.created_at,
  });

  if (detail.req_check_in || detail.req_check_out || detail.req_break_min != null) {
    events.push({
      kind: 'system',
      icon: EditCalendarRoundedIcon,
      tone: 'info',
      actor: detail.worker_name,
      title: 'specifikoi korrigjimin e kërkuar',
      at: detail.created_at,
    });
  }

  // Every comment row carries its event_type and optional meta — fan it out
  // into the unified feed alongside system rows so the UI shows one stream.
  (comments || []).forEach((c) => {
    const isReviewer = c.direction !== 'employee';
    const meta = c.meta && typeof c.meta === 'object' ? c.meta : null;
    switch (c.event_type) {
      case 'status_change': {
        const to = meta?.to || '—';
        const tone = STATUS_TONE[to] || 'info';
        events.push({
          kind: 'system',
          icon: to === 'approved' ? CheckRoundedIcon : to === 'rejected' ? BlockRoundedIcon : to === 'needs_info' ? HelpOutlineRoundedIcon : PendingActionsRoundedIcon,
          tone,
          actor: c.author_name || 'Reviewer',
          title: `ndryshoi statusin → ${STATUS_LABEL[to] || to}`,
          body: c.body || null,
          at: c.created_at,
        });
        break;
      }
      case 'assignment':
        events.push({
          kind: 'system',
          icon: AssignmentIndRoundedIcon,
          tone: 'info',
          actor: c.author_name || 'Sistemi',
          title: meta?.assigned_to_name ? `caktoi ${meta.assigned_to_name} si shqyrtues` : 'caktoi një shqyrtues',
          at: c.created_at,
        });
        break;
      case 'escalation':
        events.push({
          kind: 'system',
          icon: EscalatorWarningRoundedIcon,
          tone: 'error',
          actor: c.author_name || 'Sistemi',
          title: 'eskaloi kërkesën te HR',
          body: meta?.reason || c.body || null,
          at: c.created_at,
        });
        break;
      case 'info_request':
        events.push({
          kind: 'comment',
          tone: 'purple',
          actor: c.author_name || 'Reviewer',
          isReviewer: true,
          badge: { label: 'KËRKOI SQARIME', bg: '#F3E8FF', fg: '#6B21A8' },
          body: c.body,
          mentions: meta?.mentions || null,
          at: c.created_at,
        });
        break;
      case 'submission':
        // Already represented by the synthetic "dërgoi kërkesën" header above —
        // skip the duplicate row.
        break;
      case 'comment':
      default:
        events.push({
          kind: 'comment',
          tone: isReviewer ? 'info' : 'neutral',
          actor: c.author_name || (c.direction === 'employee' ? 'Punëtori' : 'Reviewer'),
          isReviewer,
          body: c.body,
          mentions: meta?.mentions || null,
          at: c.created_at,
        });
        break;
    }
  });

  // Trailing state — what's happening right now.
  if (detail.status === 'approved') {
    events.push({
      kind: 'system',
      icon: CheckRoundedIcon,
      tone: 'success',
      actor: detail.reviewed_by_name || 'Reviewer',
      title: 'aprovoi kërkesën',
      at: detail.reviewed_at,
    });
  } else if (detail.status === 'rejected') {
    events.push({
      kind: 'system',
      icon: BlockRoundedIcon,
      tone: 'error',
      actor: detail.reviewed_by_name || 'Reviewer',
      title: 'refuzoi kërkesën',
      at: detail.reviewed_at,
    });
  } else if (detail.status === 'needs_info') {
    events.push({
      kind: 'system',
      icon: HelpOutlineRoundedIcon,
      tone: 'purple',
      pending: true,
      title: 'Në pritje të sqarimeve nga punëtori',
    });
  } else {
    events.push({
      kind: 'system',
      icon: PendingActionsRoundedIcon,
      tone: 'warning',
      pending: true,
      title: detail.status === 'in_review' ? 'Në shqyrtim' : 'Në pritje të shqyrtimit',
    });
  }

  return (
    <Box sx={{ position: 'relative' }}>
      {events.map((e, idx) => {
        const last = idx === events.length - 1;
        const tone = TONE[e.tone] || TONE.neutral;
        return (
          <Stack key={idx} direction="row" spacing={1.25} sx={{ position: 'relative' }}>
            {/* dot/avatar + connector */}
            <Box sx={{
              position: 'relative', flexShrink: 0,
              width: 28, display: 'flex', justifyContent: 'center',
            }}>
              {e.kind === 'system' ? (
                <Box sx={{
                  width: 24, height: 24, borderRadius: '50%',
                  bgcolor: e.pending ? T.white : tone.bg,
                  border: `1.5px solid ${e.pending ? T.ink300 : tone.dot}`,
                  color: e.pending ? T.ink400 : tone.fg,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  zIndex: 1, mt: 0.125,
                }}>
                  <e.icon sx={{ fontSize: 13 }} />
                </Box>
              ) : (
                <Box sx={{ zIndex: 1 }}>
                  <Avatar name={e.actor} size={24} tone={e.isReviewer ? 'brand' : 'neutral'} />
                </Box>
              )}
              {!last && (
                <Box sx={{
                  position: 'absolute',
                  top: 24, bottom: -6, left: '50%', width: 1.5,
                  bgcolor: T.lineSoft, transform: 'translateX(-50%)',
                }} />
              )}
            </Box>

            {/* content */}
            <Box sx={{ flex: 1, minWidth: 0, pb: last ? 0 : 1.75 }}>
              <Stack direction="row" alignItems="center" spacing={0.625} sx={{ flexWrap: 'wrap' }}>
                {e.kind === 'comment' ? (
                  <>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: T.ink }}>
                      {e.actor}
                    </Typography>
                    {e.badge ? (
                      <Box sx={{
                        height: 15, px: 0.625, borderRadius: '4px',
                        fontSize: 9.5, fontWeight: 700, letterSpacing: 0.4,
                        bgcolor: e.badge.bg, color: e.badge.fg,
                        display: 'inline-flex', alignItems: 'center',
                        textTransform: 'uppercase',
                      }}>{e.badge.label}</Box>
                    ) : e.isReviewer && (
                      <Box sx={{
                        height: 15, px: 0.625, borderRadius: '4px',
                        fontSize: 9.5, fontWeight: 700, letterSpacing: 0.4,
                        bgcolor: T.brandLight, color: T.brand,
                        display: 'inline-flex', alignItems: 'center',
                        textTransform: 'uppercase',
                      }}>Reviewer</Box>
                    )}
                  </>
                ) : (
                  <Typography sx={{ fontSize: 12.5, color: T.ink700 }}>
                    {e.actor
                      ? <><Box component="span" sx={{ fontWeight: 600, color: T.ink }}>{e.actor}</Box>{' '}{e.title}</>
                      : <Box component="span" sx={{ fontWeight: 600, color: T.ink }}>{e.title}</Box>}
                  </Typography>
                )}
                {e.at && (
                  <Typography sx={{ fontSize: 11, color: T.ink400, fontVariantNumeric: 'tabular-nums' }}>
                    · {fmtDate(e.at)}
                  </Typography>
                )}
                {e.mentions?.length > 0 && (
                  <Tooltip arrow title={`Përmendet: ${e.mentions.join(', ')}`}>
                    <Box sx={{
                      height: 15, px: 0.625, borderRadius: '4px',
                      fontSize: 9.5, fontWeight: 700, letterSpacing: 0.4,
                      bgcolor: T.brandLight, color: T.brand,
                      display: 'inline-flex', alignItems: 'center', gap: 0.25,
                      textTransform: 'uppercase',
                    }}>
                      <AlternateEmailRoundedIcon sx={{ fontSize: 10 }} />
                      {e.mentions.length}
                    </Box>
                  </Tooltip>
                )}
              </Stack>
              {e.body && (
                <Typography sx={{
                  fontSize: 13, color: T.ink700,
                  whiteSpace: 'pre-wrap', lineHeight: 1.5, mt: 0.5,
                }} component="div">
                  {renderWithMentions(e.body)}
                </Typography>
              )}
            </Box>
          </Stack>
        );
      })}

      {comments.length === 0 && (
        <Box sx={{ mt: 2 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{
            px: 1.5, py: 1.25,
            borderRadius: '10px',
            border: `1px dashed ${T.line}`,
            bgcolor: T.white,
          }}>
            <ForumOutlinedIcon sx={{ fontSize: 16, color: T.ink400 }} />
            <Typography sx={{ fontSize: 12, color: T.ink500 }}>
              Asnjë koment akoma. Shkruaj më poshtë për të nisur bisedën.
            </Typography>
          </Stack>
        </Box>
      )}
    </Box>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
 * Workspace: Create
 * ──────────────────────────────────────────────────────────────────────────── */
function WorkspaceCreate({ form, setForm, onCancel, onSubmit, workerName }) {
  const inputSx = {
    '& .MuiOutlinedInput-root': {
      height: 38, borderRadius: '10px', bgcolor: T.white, fontSize: 13.5,
      '& fieldset': { borderColor: T.line },
      '&:hover fieldset': { borderColor: T.ink300 },
      '&.Mui-focused fieldset': { borderColor: T.brand, borderWidth: 1 },
      '&.Mui-focused': { boxShadow: `0 0 0 3px ${alpha(T.brand, 0.12)}` },
    },
  };
  const textareaSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '10px', bgcolor: T.white, fontSize: 13.5,
      '& fieldset': { borderColor: T.line },
      '&:hover fieldset': { borderColor: T.ink300 },
      '&.Mui-focused fieldset': { borderColor: T.brand, borderWidth: 1 },
      '&.Mui-focused': { boxShadow: `0 0 0 3px ${alpha(T.brand, 0.12)}` },
    },
  };
  const isValid = form.request_date && form.description.trim();

  return (
    <>
      {/* Header */}
      <Box sx={{
        px: { xs: 1.75, md: 2.5 }, py: 1.75,
        borderBottom: `1px solid ${T.line}`,
        bgcolor: T.white, flexShrink: 0,
      }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1.5}>
          <Stack direction="row" alignItems="center" spacing={1.25}>
            <IconButton
              onClick={onCancel}
              sx={{
                display: { xs: 'inline-flex', lg: 'none' },
                width: 32, height: 32, borderRadius: '8px',
                color: T.ink500,
                '&:hover': { bgcolor: T.surfaceAlt },
              }}
            ><ArrowBackRoundedIcon sx={{ fontSize: 17 }} /></IconButton>
            <Box>
              <Typography sx={{ fontSize: 16, fontWeight: 700, color: T.ink, lineHeight: 1.2 }}>
                Kërkesë e re korrigjimi
              </Typography>
              <Typography sx={{ fontSize: 12.5, color: T.ink500, mt: 0.25 }}>
                Plotëso detajet për {workerName}
              </Typography>
            </Box>
          </Stack>
          <IconButton
            onClick={onCancel}
            sx={{
              display: { xs: 'none', lg: 'inline-flex' },
              width: 32, height: 32, borderRadius: '8px',
              color: T.ink500,
              '&:hover': { bgcolor: T.surfaceAlt, color: T.ink },
            }}
          ><CloseRoundedIcon sx={{ fontSize: 17 }} /></IconButton>
        </Stack>
      </Box>

      {/* Body — flat sections (no step circles, content-sized) */}
      <Box sx={{
        overflowY: 'auto',
        px: { xs: 1.75, md: 2.5 }, py: 1.75,
      }}>
        {/* — Section: Type + date + priority — */}
        <FormSection title="Lloji dhe data">
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
            <FormControl size="small" sx={{ flex: 1 }}>
              <InputLabel sx={{ fontSize: 13 }}>Lloji i kërkesës</InputLabel>
              <Select
                label="Lloji i kërkesës"
                value={form.type}
                onChange={(e) => setForm(p => ({ ...p, type: e.target.value }))}
                sx={{
                  borderRadius: '10px', bgcolor: T.white,
                  fontSize: 13.5, height: 38,
                  '& fieldset': { borderColor: T.line },
                }}
              >
                {TYPE_OPTS.map(t => (
                  <MenuItem key={t.value} value={t.value} sx={{ fontSize: 13.5 }}>{t.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              size="small"
              type="date"
              label="Data për korrigjim"
              InputLabelProps={{ shrink: true, sx: { fontSize: 13 } }}
              value={form.request_date}
              onChange={(e) => setForm(p => ({ ...p, request_date: e.target.value }))}
              required
              sx={{ ...inputSx, flex: 1 }}
            />
            <FormControl size="small" sx={{ flex: 0.5, minWidth: 140 }}>
              <InputLabel sx={{ fontSize: 13 }}>Prioriteti</InputLabel>
              <Select
                label="Prioriteti"
                value={form.priority || 'normal'}
                onChange={(e) => setForm(p => ({ ...p, priority: e.target.value }))}
                sx={{
                  borderRadius: '10px', bgcolor: T.white,
                  fontSize: 13.5, height: 38,
                  '& fieldset': { borderColor: T.line },
                }}
              >
                {PRIORITY_OPTS.map(p => (
                  <MenuItem key={p.value} value={p.value} sx={{ fontSize: 13.5 }}>
                    <Stack direction="row" alignItems="center" spacing={0.875}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: p.dot }} />
                      <span>{p.label}</span>
                    </Stack>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </FormSection>

        {/* — Section: Time grid — */}
        <FormSection title="Orari i kërkuar" hint="Plotëso vetëm fushat që duhen ndryshuar.">
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 110px' },
            gap: 1.25,
          }}>
            <TextField
              size="small" type="time" label="Check-in"
              InputLabelProps={{ shrink: true, sx: { fontSize: 13 } }}
              value={form.req_check_in_time}
              onChange={(e) => setForm(p => ({ ...p, req_check_in_time: e.target.value }))}
              sx={inputSx}
            />
            <TextField
              size="small" type="time" label="Check-out"
              InputLabelProps={{ shrink: true, sx: { fontSize: 13 } }}
              value={form.req_check_out_time}
              onChange={(e) => setForm(p => ({ ...p, req_check_out_time: e.target.value }))}
              sx={inputSx}
            />
            <TextField
              size="small"
              type="number" label="Pauzë (min)"
              inputProps={{ min: 0, max: 240 }}
              InputLabelProps={{ sx: { fontSize: 13 } }}
              value={form.req_break_min}
              onChange={(e) => setForm(p => ({ ...p, req_break_min: e.target.value }))}
              sx={inputSx}
            />
          </Box>
        </FormSection>

        {/* — Section: Reason — */}
        <FormSection title="Arsyeja" required>
          <TextField
            size="small" fullWidth multiline rows={3}
            placeholder="Shkruaj arsyen e detajuar të kërkesës…"
            value={form.description}
            onChange={(e) => setForm(p => ({ ...p, description: e.target.value }))}
            required
            sx={textareaSx}
          />
        </FormSection>
      </Box>

      {/* Footer */}
      <Box sx={{
        px: { xs: 1.75, md: 2.5 }, py: 1.5,
        borderTop: `1px solid ${T.line}`,
        bgcolor: T.white, flexShrink: 0,
      }}>
        <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center">
          <Typography sx={{ fontSize: 11.5, color: isValid ? T.successInk : T.ink400, mr: 'auto' }}>
            {isValid ? '✓ Gati për dërgim' : 'Data dhe arsyeja janë të detyrueshme'}
          </Typography>
          <Button
            onClick={onCancel}
            sx={{
              height: 34, px: 1.5, borderRadius: '8px',
              bgcolor: T.white, color: T.ink700,
              border: `1px solid ${T.line}`,
              textTransform: 'none', fontSize: 13, fontWeight: 500,
              '&:hover': { bgcolor: T.surface, borderColor: T.ink300 },
            }}
          >Anulo</Button>
          <Button
            onClick={onSubmit}
            disabled={!isValid}
            startIcon={<SendRoundedIcon sx={{ fontSize: 15 }} />}
            disableElevation
            sx={{
              height: 34, px: 1.75, borderRadius: '8px',
              bgcolor: T.brand, color: T.white,
              textTransform: 'none', fontSize: 13, fontWeight: 600,
              '&:hover': { bgcolor: T.brandHover },
              '&.Mui-disabled': { bgcolor: T.surfaceAlt, color: T.ink400 },
            }}
          >Dërgo kërkesën</Button>
        </Stack>
      </Box>
    </>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
 * Small helpers
 * ──────────────────────────────────────────────────────────────────────────── */
function MetaRow({ label, value }) {
  return (
    <Box>
      <Typography sx={{
        fontSize: 10.5, fontWeight: 700, color: T.ink400,
        textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.375,
      }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: T.ink, lineHeight: 1.35 }}>
        {value || '—'}
      </Typography>
    </Box>
  );
}

function ValueTile({ icon: Icon, tone, label, value }) {
  const c = TONE[tone] || TONE.neutral;
  return (
    <Box sx={{
      px: 1.5, py: 1,
      borderRadius: '10px',
      bgcolor: T.white,
      border: `1px solid ${T.line}`,
      display: 'flex', alignItems: 'center', gap: 1.25,
    }}>
      <Box sx={{
        width: 32, height: 32, borderRadius: '8px',
        bgcolor: c.bg, color: c.fg,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0,
      }}>
        <Icon sx={{ fontSize: 16 }} />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{
          fontSize: 10.5, fontWeight: 700, color: T.ink400,
          textTransform: 'uppercase', letterSpacing: 0.4,
        }}>
          {label}
        </Typography>
        <Typography sx={{
          fontSize: 14.5, fontWeight: 700, color: T.ink,
          fontVariantNumeric: 'tabular-nums', lineHeight: 1.25,
        }}>
          {value}
        </Typography>
      </Box>
    </Box>
  );
}

/* ── Workflow stage: compact card showing approval state ──────────────────── */
function WorkflowStage({ label, name, timestamp, state }) {
  const stateConfig = {
    completed: { tone: TONE.info,    icon: SendRoundedIcon,   accent: T.brand },
    approved:  { tone: TONE.success, icon: CheckRoundedIcon, accent: T.successInk },
    rejected:  { tone: TONE.error,   icon: BlockRoundedIcon, accent: T.errorInk },
    pending:   { tone: TONE.warning, icon: PendingActionsRoundedIcon, accent: T.warningInk },
  };
  const cfg = stateConfig[state] || stateConfig.pending;
  const Icon = cfg.icon;
  return (
    <Box sx={{
      px: 1.5, py: 1,
      borderRadius: '10px',
      bgcolor: T.white,
      border: `1px solid ${T.line}`,
      display: 'flex', alignItems: 'center', gap: 1.25,
    }}>
      <Box sx={{
        width: 32, height: 32, borderRadius: '8px',
        bgcolor: cfg.tone.bg, color: cfg.tone.fg,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0,
      }}>
        <Icon sx={{ fontSize: 16 }} />
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography sx={{
          fontSize: 10.5, fontWeight: 700, color: T.ink400,
          textTransform: 'uppercase', letterSpacing: 0.4,
        }}>
          {label}
        </Typography>
        <Typography sx={{
          fontSize: 13, fontWeight: 600, color: T.ink, lineHeight: 1.3,
        }} noWrap>
          {name}
        </Typography>
        {timestamp && (
          <Typography sx={{ fontSize: 11, color: T.ink400, mt: 0.125 }}>
            {fmtDate(timestamp)}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

/* ── Comparison tile: OLD → NEW (Jira-style diff) ───────────────────────────
 * Renders side-by-side original vs requested value with an arrow.
 * Falls back to a single ValueTile-style cell when no original exists.
 * ────────────────────────────────────────────────────────────────────────── */
function ComparisonTile({ icon: Icon, tone, label, oldValue, newValue }) {
  const c = TONE[tone] || TONE.neutral;
  const hasOld = oldValue !== undefined && oldValue !== null && String(oldValue).trim() !== '' && oldValue !== '—';
  return (
    <Box sx={{
      px: 1.5, py: 1,
      borderRadius: '10px',
      bgcolor: T.white,
      border: `1px solid ${T.line}`,
    }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: hasOld ? 0.75 : 0 }}>
        <Box sx={{
          width: 24, height: 24, borderRadius: '6px',
          bgcolor: c.bg, color: c.fg,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}>
          <Icon sx={{ fontSize: 14 }} />
        </Box>
        <Typography sx={{
          fontSize: 10.5, fontWeight: 700, color: T.ink400,
          textTransform: 'uppercase', letterSpacing: 0.4,
        }}>
          {label}
        </Typography>
      </Stack>
      {hasOld ? (
        <Stack direction="row" alignItems="center" spacing={0.875}>
          <Typography sx={{
            fontSize: 13, color: T.ink500, fontWeight: 500,
            fontVariantNumeric: 'tabular-nums',
            textDecoration: 'line-through',
            textDecorationColor: T.ink300,
          }}>
            {oldValue}
          </Typography>
          <ArrowForwardRoundedIcon sx={{ fontSize: 13, color: T.ink400 }} />
          <Typography sx={{
            fontSize: 15, fontWeight: 700, color: c.fg,
            fontVariantNumeric: 'tabular-nums', lineHeight: 1.25,
          }}>
            {newValue}
          </Typography>
        </Stack>
      ) : (
        <Typography sx={{
          fontSize: 15, fontWeight: 700, color: T.ink,
          fontVariantNumeric: 'tabular-nums', lineHeight: 1.25,
        }}>
          {newValue}
        </Typography>
      )}
    </Box>
  );
}

function FormSection({ title, hint, required, children }) {
  return (
    <Box sx={{ '& + &': { mt: 1.75 } }}>
      <Stack direction="row" alignItems="baseline" spacing={0.75} sx={{ mb: 1 }}>
        <Typography sx={{
          fontSize: 11, fontWeight: 600, color: T.ink500,
          textTransform: 'uppercase', letterSpacing: 0.5,
        }}>
          {title}{required && <Box component="span" sx={{ color: T.errorInk, ml: 0.25 }}>*</Box>}
        </Typography>
        {hint && (
          <Typography sx={{ fontSize: 11, color: T.ink400 }}>· {hint}</Typography>
        )}
      </Stack>
      {children}
    </Box>
  );
}

// Kept for backward compat — alias (unused after FieldGroup removal)
function FieldGroup({ step, title, hint, children }) {
  return <FormSection title={title} hint={hint}>{children}</FormSection>;
}

/* ─────────────────────────────────────────────────────────────────────────────
 * WorkflowStepper — visualises the 4-stage approval pipeline
 *   Employee  →  Team Lead  →  HR  →  Final
 * State per stage: completed (✓), active (pulse), pending (hollow), rejected (✕)
 * ──────────────────────────────────────────────────────────────────────────── */
function WorkflowStepper({ detail }) {
  const status = detail.status;
  const isRejected   = status === 'rejected';
  const isApproved   = status === 'approved';
  const isReview     = status === 'in_review';
  const isNeedsInfo  = status === 'needs_info';
  const isOpen       = status === 'open';
  const escalated    = !!detail.escalated_to_hr;

  // Submission is always complete the moment the row exists.
  const submitted = {
    label: 'Punëtori',
    desc:  'Dërgoi kërkesën',
    name:  detail.worker_name,
    timestamp: detail.created_at,
    state: 'completed',
  };

  // Team-lead stage covers everything between submission and final decision
  // for non-escalated requests. Once escalated, the team lead is "done" and
  // HR is "active".
  const teamLead = {
    label: 'Team Lead',
    desc:  detail.assigned_to_name ? `Caktuar te ${detail.assigned_to_name}` : 'Shqyrtimi i parë',
    name:  detail.assigned_to_name || (isManagerSeen(detail) ? detail.reviewed_by_name : 'Pa caktuar'),
    timestamp: detail.assigned_at || (escalated ? detail.escalated_at : (isApproved || isRejected ? detail.reviewed_at : null)),
    state:
      isRejected && !escalated ? 'rejected' :
      isApproved && !escalated ? 'completed' :
      escalated                ? 'completed' :
      isReview || isNeedsInfo  ? 'active'    :
      isOpen                   ? 'active'    :
                                 'pending',
  };

  const hr = {
    label: 'HR',
    desc:  escalated ? 'Eskaluar nga team lead' : 'Aprovim final',
    name:  escalated ? (detail.reviewed_by_name || 'HR e papërcaktuar') : (isApproved || isRejected ? detail.reviewed_by_name : '—'),
    timestamp: escalated ? (isApproved || isRejected ? detail.reviewed_at : null) : (isApproved || isRejected ? detail.reviewed_at : null),
    state:
      isRejected && escalated ? 'rejected' :
      isApproved && escalated ? 'completed' :
      escalated && (isReview || isNeedsInfo) ? 'active' :
      isApproved || isRejected ? 'completed' :
      'pending',
  };

  const final = {
    label: 'Përfundimi',
    desc:  isApproved ? 'Kërkesa u aprovua' :
           isRejected ? 'Kërkesa u refuzua' :
           isNeedsInfo ? 'Në pritje të sqarimeve' :
           'Pritet aprovimi',
    name:  isApproved || isRejected ? detail.reviewed_by_name : '—',
    timestamp: detail.reviewed_at,
    state:
      isApproved ? 'completed' :
      isRejected ? 'rejected'  :
      'pending',
  };

  const stages = [submitted, teamLead, hr, final];

  return (
    <Box sx={{
      display: 'grid',
      gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
      gap: { xs: 0.75, md: 1 },
      mb: 0.5,
    }}>
      {stages.map((s, i) => (
        <StageCard key={s.label} stage={s} index={i + 1} isLast={i === stages.length - 1} />
      ))}
    </Box>
  );
}

// Helper: did a reviewer touch this request? Used to label the team-lead card.
function isManagerSeen(detail) {
  return Boolean(detail.reviewed_by_name || detail.assigned_to_name);
}

function StageCard({ stage, index, isLast }) {
  const cfg = {
    completed: { icon: CheckRoundedIcon,           tone: TONE.success, ring: T.success,   pulse: false },
    active:    { icon: PendingActionsRoundedIcon,  tone: TONE.warning, ring: T.warning,   pulse: true  },
    pending:   { icon: HelpOutlineRoundedIcon,     tone: TONE.neutral, ring: T.ink300,    pulse: false },
    rejected:  { icon: BlockRoundedIcon,           tone: TONE.error,   ring: T.error,     pulse: false },
  }[stage.state] || { icon: HelpOutlineRoundedIcon, tone: TONE.neutral, ring: T.ink300, pulse: false };
  const Icon = cfg.icon;
  return (
    <Box sx={{
      position: 'relative',
      px: 1.25, py: 1.125,
      borderRadius: '10px',
      bgcolor: T.white,
      border: `1px solid ${stage.state === 'active' ? cfg.ring : T.line}`,
      boxShadow: stage.state === 'active' ? `0 0 0 3px ${alpha(cfg.ring, 0.15)}` : 'none',
      display: 'flex', alignItems: 'center', gap: 1.125,
      transition: 'box-shadow 200ms ease, border-color 200ms ease',
    }}>
      <Box sx={{
        position: 'relative', flexShrink: 0,
        width: 30, height: 30, borderRadius: '50%',
        bgcolor: cfg.tone.bg, color: cfg.tone.fg,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        '&::after': cfg.pulse ? {
          content: '""',
          position: 'absolute', inset: -3, borderRadius: '50%',
          border: `2px solid ${cfg.ring}`,
          animation: 'attCorrPulse 1.5s ease-out infinite',
          opacity: 0.5,
        } : null,
        '@keyframes attCorrPulse': {
          '0%':   { transform: 'scale(0.95)', opacity: 0.55 },
          '100%': { transform: 'scale(1.35)', opacity: 0 },
        },
      }}>
        <Icon sx={{ fontSize: 14 }} />
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Stack direction="row" alignItems="center" spacing={0.625} sx={{ mb: 0.125 }}>
          <Typography sx={{
            fontSize: 9.5, fontWeight: 700, color: T.ink400,
            textTransform: 'uppercase', letterSpacing: 0.5,
          }}>
            Hapi {index}
          </Typography>
          <Box sx={{ width: 2, height: 2, borderRadius: '50%', bgcolor: T.ink300 }} />
          <Typography sx={{
            fontSize: 10, fontWeight: 600, color: cfg.tone.fg,
            textTransform: 'uppercase', letterSpacing: 0.3,
          }}>
            {stage.label}
          </Typography>
        </Stack>
        <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: T.ink, lineHeight: 1.3 }} noWrap>
          {stage.name || '—'}
        </Typography>
        <Typography sx={{ fontSize: 11, color: T.ink500, lineHeight: 1.3 }} noWrap>
          {stage.desc}
        </Typography>
        {stage.timestamp && (
          <Typography sx={{ fontSize: 10.5, color: T.ink400, mt: 0.125, fontVariantNumeric: 'tabular-nums' }}>
            {fmtDate(stage.timestamp)}
          </Typography>
        )}
      </Box>
      {/* Connector chevron between cards (md+ only) */}
      {!isLast && (
        <Box
          aria-hidden
          sx={{
            display: { xs: 'none', md: 'flex' },
            position: 'absolute', right: -10, top: '50%',
            transform: 'translateY(-50%)',
            width: 20, height: 20, borderRadius: '50%',
            bgcolor: T.surface, color: T.ink400,
            alignItems: 'center', justifyContent: 'center',
            zIndex: 2,
            border: `1px solid ${T.lineSoft}`,
          }}
        >
          <ArrowForwardRoundedIcon sx={{ fontSize: 12 }} />
        </Box>
      )}
    </Box>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
 * AssignDialog — pick a reviewer from the workers list
 * ──────────────────────────────────────────────────────────────────────────── */
function AssignDialog({ open, workers, onClose, onAssign }) {
  const [q, setQ] = useState('');
  useEffect(() => { if (!open) setQ(''); }, [open]);
  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return (workers || [])
      .filter(w => {
        const name = `${w.first_name || w.FirstName || ''} ${w.last_name || w.LastName || ''}`.trim();
        if (!term) return true;
        return (
          name.toLowerCase().includes(term) ||
          (w.email || w.Email || '').toLowerCase().includes(term) ||
          (w.position || w.Position || '').toLowerCase().includes(term)
        );
      })
      .slice(0, 50);
  }, [workers, q]);
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontSize: 15, fontWeight: 700, color: T.ink }}>
        Cakto shqyrtues
      </DialogTitle>
      <DialogContent dividers sx={{ p: 1.5 }}>
        <TextField
          autoFocus fullWidth size="small"
          placeholder="Kërko emër, email ose pozitë…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16, color: T.ink400 }} /></InputAdornment>,
          }}
          sx={{ mb: 1.25, '& .MuiOutlinedInput-root': { fontSize: 13, borderRadius: '8px' } }}
        />
        <Box sx={{ maxHeight: 320, overflowY: 'auto' }}>
          {filtered.length === 0 ? (
            <Typography sx={{ p: 2, textAlign: 'center', fontSize: 12.5, color: T.ink500 }}>
              {q ? 'Asnjë rezultat' : 'Lista e punëtorëve është bosh'}
            </Typography>
          ) : (
            filtered.map(w => {
              const id = w.id || w.Id;
              const name = `${w.first_name || w.FirstName || ''} ${w.last_name || w.LastName || ''}`.trim();
              const role = w.position || w.Position || w.role || w.Role || '';
              return (
                <Box
                  key={id}
                  onClick={() => onAssign(id, name)}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 1.25,
                    px: 1, py: 0.875, borderRadius: '8px',
                    cursor: 'pointer',
                    '&:hover': { bgcolor: T.surfaceAlt },
                  }}
                >
                  <Avatar name={name} size={28} />
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, color: T.ink, lineHeight: 1.3 }} noWrap>
                      {name || '—'}
                    </Typography>
                    {role && (
                      <Typography sx={{ fontSize: 11, color: T.ink500, lineHeight: 1.3 }} noWrap>
                        {role}
                      </Typography>
                    )}
                  </Box>
                </Box>
              );
            })
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 1.5 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none', fontSize: 13 }}>Anulo</Button>
      </DialogActions>
    </Dialog>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
 * EscalateDialog — capture an escalation reason before flipping the bit
 * ──────────────────────────────────────────────────────────────────────────── */
function EscalateDialog({ open, onClose, onConfirm }) {
  const [reason, setReason] = useState('');
  useEffect(() => { if (!open) setReason(''); }, [open]);
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontSize: 15, fontWeight: 700, color: T.ink }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <EscalatorWarningRoundedIcon sx={{ fontSize: 18, color: T.errorInk }} />
          <span>Eskalo te HR</span>
        </Stack>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 1.75 }}>
        <Typography sx={{ fontSize: 12.5, color: T.ink500, mb: 1.25, lineHeight: 1.5 }}>
          Kjo kërkesë do të kalojë te HR për aprovim final. HR do të njoftohet me email dhe Slack.
        </Typography>
        <TextField
          autoFocus fullWidth multiline minRows={3}
          placeholder="Pse po e eskalon? (opsional)"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          sx={{ '& .MuiOutlinedInput-root': { fontSize: 13, borderRadius: '8px' } }}
        />
      </DialogContent>
      <DialogActions sx={{ p: 1.5 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none', fontSize: 13 }}>Anulo</Button>
        <Button
          variant="contained"
          onClick={() => onConfirm(reason.trim())}
          startIcon={<EscalatorWarningRoundedIcon sx={{ fontSize: 15 }} />}
          sx={{
            textTransform: 'none', fontSize: 13, fontWeight: 600,
            bgcolor: T.errorInk, '&:hover': { bgcolor: T.error },
          }}
        >Eskalo</Button>
      </DialogActions>
    </Dialog>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
 * AuditLogDialog — append-only event history fetched from /audit
 * ──────────────────────────────────────────────────────────────────────────── */
function AuditLogDialog({ open, loading, rows, onClose }) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontSize: 15, fontWeight: 700, color: T.ink }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <HistoryRoundedIcon sx={{ fontSize: 18, color: T.ink500 }} />
          <span>Regjistri i auditimit</span>
        </Stack>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        {loading ? (
          <Stack spacing={1.25} sx={{ p: 2 }}>
            {[1,2,3].map(i => <Skeleton key={i} variant="rounded" height={48} />)}
          </Stack>
        ) : rows.length === 0 ? (
          <Typography sx={{ p: 3, textAlign: 'center', fontSize: 12.5, color: T.ink500 }}>
            Asnjë regjistrim auditimi për këtë kërkesë.
          </Typography>
        ) : (
          <Box>
            {rows.map((row) => (
              <Box
                key={row.id}
                sx={{
                  px: 2, py: 1.25,
                  borderBottom: `1px solid ${T.lineSoft}`,
                  '&:last-of-type': { borderBottom: 0 },
                }}
              >
                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: T.ink }}>
                      {row.action || row.event_type}
                    </Typography>
                    <Typography sx={{ fontSize: 11, color: T.ink500 }}>
                      {row.user_email || 'system'}
                    </Typography>
                  </Box>
                  <Typography sx={{ fontSize: 11, color: T.ink400, fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>
                    {fmtDate(row.created_at)}
                  </Typography>
                </Stack>
                {(row.after_data || row.before_data) && (
                  <Box
                    component="pre"
                    sx={{
                      mt: 0.75, mb: 0, p: 1,
                      bgcolor: T.surface, border: `1px solid ${T.lineSoft}`, borderRadius: '6px',
                      fontSize: 10.5, color: T.ink700, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                      whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                      maxHeight: 100, overflowY: 'auto',
                    }}
                  >
                    {JSON.stringify(row.after_data || row.before_data, null, 2)}
                  </Box>
                )}
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 1.5 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none', fontSize: 13 }}>Mbyll</Button>
      </DialogActions>
    </Dialog>
  );
}
